/**
 * pathBuilderGraphSlice — Redux state v2 for the React Flow DAG builder (M3).
 *
 * Design: docs/learning-center/designs/learning-paths-builder-rebuild.md §2,
 * as amended (canvasX/canvasY naming, hyphenated connection literals,
 * granular endpoints, canonical GraphResponse). Registered in rootReducer
 * alongside the legacy pathBuilderSlice; the flow-builder feature flag picks
 * which builder UI (and therefore which slice) is live.
 *
 * Core invariants:
 * - Graph entities are normalized and keyed by clientId (uuid). serverId is
 *   paired in when the creating mutation resolves; refetches REUSE existing
 *   clientIds for known serverIds so React Flow keys stay stable.
 * - All mutations flow through a FIFO queue dispatched strictly serially.
 *   Ops that reference an entity whose serverId is still null implicitly
 *   block: FIFO ordering guarantees the creating op resolves first, and the
 *   executing op resolves serverIds from fresh state at execution time.
 * - Every mutation request carries expectedUpdatedAt; every success response
 *   updates meta.serverUpdatedAt. Any failure (including 409 stale) pauses
 *   the queue, DROPS all pending ops, refetches GET /graph (truth restore),
 *   toasts, and resumes with an empty queue.
 * - Position changes are debounced 500ms and coalesced into ONE batch
 *   PATCH /steps/positions (latest coordinates per step win).
 * - isDirty == save.pendingOps > 0 (selectGraphIsDirty).
 *
 * M4 additions (full authoring):
 * - Op surface widened: addItem (all 8 concrete types), updateItem,
 *   deleteItem, moveItem, reorderWithinStep, addConnection,
 *   updateConnection, deleteConnection, createStepWithItem (compound
 *   pane-drop: create step THEN add item, dependency-blocked by FIFO).
 * - clientId↔serverId PAIRING MAP survives optimistic entity removal so
 *   late-bound ops (undo deletes) resolve serverIds at execution time.
 * - UNDO/REDO: command history (depth 50) over STRUCTURAL ops only —
 *   position drags and selection are excluded. Each op captures its
 *   inverse AT DISPATCH TIME; undo/redo replay through the SAME queue.
 *   History clears on load, on failure truth-restore, and on
 *   un-invertible ops (user deleteStep; moveItem that empties+deletes its
 *   source step).
 * - duplicateCurrentPath is a DIRECT thunk (POST /duplicate), not a queue
 *   op.
 *
 * Public API (wave-2 canvas/palette/rail agents code against this):
 * - Thunks: loadPathGraph, resetBuilder, enqueueCreateStep,
 *   enqueueUpdateStep, enqueueDeleteStep, enqueueAddItem,
 *   enqueueAddCourseItem, enqueueUpdateItem, enqueueDeleteItem,
 *   enqueueMoveItem, enqueueReorderStepItems, enqueueAddConnection,
 *   enqueueUpdateConnection, enqueueDeleteConnection,
 *   enqueueCreateStepWithItem, undoGraphEdit, redoGraphEdit,
 *   duplicateCurrentPath, queueStepPositionChange,
 *   flushStepPositionChanges
 * - Actions: selectionChanged, selectionCleared, graphLoaded
 * - Selectors: selectGraphPathId, selectGraphLoadStatus, selectGraphLoadError,
 *   selectGraphMeta, selectBuilderSteps, selectBuilderConnections,
 *   selectBuilderItems, selectOrderedSteps, selectConnectionList,
 *   selectStepItems, selectBuilderSelection, selectGraphSaveState,
 *   selectGraphIsDirty, selectServerUpdatedAt, selectCanUndo,
 *   selectCanRedo, selectUndoDepth, selectRedoDepth
 */

import {
  createSelector,
  createSlice,
  nanoid,
  PayloadAction,
  ThunkAction,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import {
  addItemToStep,
  createGraphConnection,
  createStep,
  deleteGraphConnection,
  deleteGraphItem,
  deleteStep,
  duplicateLearningPath,
  getPathGraph,
  moveGraphItem,
  reorderStepItems,
  updateGraphConnection,
  updateGraphItem,
  updateStep,
  updateStepPositions,
} from "@/api/pathGraph";
import type {
  AddItemToStepRequest,
  GraphAuthorableConnectionType,
  GraphConditionBranch,
  GraphConnection,
  GraphConnectionType,
  GraphItemType,
  GraphResponse,
  GraphRoleTag,
  GraphStep,
  GraphStepItem,
  GraphTradeTag,
  PathGraphStatus,
  StepPositionInput,
  UpdateGraphConnectionFields,
  UpdateGraphItemFields,
} from "@/api/pathGraph";
import { toast } from "@/components/ui/use-toast";
import type { LearningPathResponse } from "../apiTypes";

// learningPathsSlice's updatePath thunk, matched by type: importing the thunk
// would drag axios into this slice's Jest graph (CJS transform breaks).
const UPDATE_PATH_FULFILLED = "learningPaths/updatePath/fulfilled";

// --- State shapes (builder design §2 as amended) ------------------------------

export type GraphLoadStatus = "idle" | "loading" | "loaded" | "error";
export type GraphSaveStatus = "idle" | "saving" | "saved" | "error";
/** "branch" = a UI-only branch block (id: branchNodeId(step, type)). */
export type SelectionKind = "step" | "item" | "connection" | "branch" | "path";

export interface BuilderMeta {
  name: string;
  description: string | null;
  companyId: number | null;
  status: PathGraphStatus;
  isArchived: boolean;
  selfEnrollmentEnabled: boolean;
  enforceOrder: boolean;
  /** Audience (settings menu); [] = everyone. */
  growthStages: string[];
  trades: string[];
  roles: string[];
  /** Path metadata (details dialog). */
  showInDirectory: boolean;
  estimatedMinutes: number | null;
  learningObjectives: string[];
  thumbnailUrl: string | null;
  /** Concurrency token; sent as expectedUpdatedAt on every mutation. */
  serverUpdatedAt: string;
}

export interface BuilderStep {
  clientId: string;
  serverId: number | null;
  label: string | null;
  /** Kicker above the step title in the learner rail; null/absent = "Step N". */
  partLabel?: string | null;
  requireAll: boolean;
  sortKey: number;
  /** Per-step time-release gate: days after enrollment before unlock; null/absent = none. */
  unlockAfterDays?: number | null;
  /** Per-step memory gate: locked until the Memory variable's answer satisfies the rule; null = none. */
  unlockMemoryKey?: string | null;
  unlockMemoryRule?: string | null;
  /** null = dagre auto-layout. */
  canvasX: number | null;
  canvasY: number | null;
  /** Item clientIds, ordered by position. */
  itemIds: string[];
}

/** Connection type literals are the canonical hyphenated set (amendment #3). */
export interface BuilderConnection {
  clientId: string;
  serverId: number | null;
  /** Step clientId. */
  fromStepId: string;
  /** Step clientId. */
  toStepId: string;
  type: GraphConnectionType;
  roleTag?: GraphRoleTag;
  tradeTag?: GraphTradeTag;
  conditionBranch?: GraphConditionBranch;
  scoreThreshold?: number;
  /** Item clientId of the quiz-bearing source item. */
  conditionSourceItemId?: string;
}

export interface BuilderItem {
  clientId: string;
  serverId: number | null;
  /** Owning step's clientId. */
  stepClientId: string;
  type: GraphItemType;
  position: number;
  /** Display label while an optimistic create is in flight (payload null). */
  pendingTitle: string | null;
  /** Full server item DTO once known; null while creation is in flight. */
  payload: GraphStepItem | null;
}

export interface BuilderSelection {
  kind: SelectionKind | null;
  /** clientId of the selected entity. */
  id: string | null;
}

export interface BuilderSaveState {
  status: GraphSaveStatus;
  /** Queued + in-flight mutation count. isDirty == pendingOps > 0. */
  pendingOps: number;
  lastSavedAt: string | null;
  lastError: string | null;
}

/** Per-type add-item payload — the wire request minus the concurrency token. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type AddItemDraft = DistributiveOmit<
  AddItemToStepRequest,
  "expectedUpdatedAt"
>;

export interface StepChanges {
  label?: string | null;
  partLabel?: string | null;
  requireAll?: boolean;
  /** Time-release gate in days; 0 clears the gate (engine treats 0 = no delay). */
  unlockAfterDays?: number | null;
  /** Memory gate; set both together, null key clears it (sent to the server as ""). */
  unlockMemoryKey?: string | null;
  unlockMemoryRule?: string | null;
  canvasX?: number | null;
  canvasY?: number | null;
}

export type QueueOp =
  | {
      opId: string;
      kind: "createStep";
      stepClientId: string;
      label: string | null;
      requireAll: boolean;
      canvasX: number | null;
      canvasY: number | null;
    }
  | {
      opId: string;
      kind: "updateStep";
      stepClientId: string;
      changes: StepChanges;
    }
  | {
      opId: string;
      kind: "updateStepPositions";
      positions: Array<{
        stepClientId: string;
        canvasX: number | null;
        canvasY: number | null;
      }>;
    }
  | {
      opId: string;
      kind: "deleteStep";
      stepClientId: string;
      /**
       * Captured at enqueue when known; null = late-bound (undo of a create
       * still in flight) — resolved via the pairing map at execution time.
       */
      stepServerId: number | null;
    }
  | {
      opId: string;
      kind: "addItem";
      stepClientId: string;
      itemClientId: string;
      draft: AddItemDraft;
    }
  | {
      opId: string;
      kind: "updateItem";
      itemClientId: string;
      changes: UpdateGraphItemFields;
    }
  | {
      opId: string;
      kind: "deleteItem";
      itemClientId: string;
    }
  | {
      opId: string;
      kind: "moveItem";
      itemClientId: string;
      fromStepClientId: string;
      toStepClientId: string;
      /** PATH-WIDE position (server convention); omitted = end-of-step. */
      position?: number;
      keepEmptyStep?: boolean;
    }
  | {
      opId: string;
      kind: "reorderWithinStep";
      stepClientId: string;
      /** COMPLETE permutation of the step's items, in new display order. */
      orderedItemClientIds: string[];
    }
  | {
      opId: string;
      kind: "addConnection";
      connectionClientId: string;
      fromStepClientId: string;
      toStepClientId: string;
      type: GraphAuthorableConnectionType | "conditional";
      roleTag?: GraphRoleTag;
      tradeTag?: GraphTradeTag;
      /** Conditional (value mode): source item clientId + rule / option key. */
      conditionSourceItemClientId?: string;
      conditionBranch?: GraphConditionBranch;
    }
  | {
      opId: string;
      kind: "updateConnection";
      connectionClientId: string;
      changes: UpdateGraphConnectionFields;
    }
  | {
      opId: string;
      kind: "deleteConnection";
      connectionClientId: string;
    };

/**
 * Replayable command — the unit of undo/redo history. Commands carry
 * explicit clientIds so a redo recreates entities under their ORIGINAL ids,
 * keeping deeper history entries (which reference those ids) valid.
 */
export type BuilderCommand =
  | {
      kind: "createStep";
      stepClientId: string;
      label: string | null;
      requireAll: boolean;
      canvasX: number | null;
      canvasY: number | null;
    }
  | { kind: "updateStep"; stepClientId: string; changes: StepChanges }
  | { kind: "deleteStep"; stepClientId: string }
  | {
      kind: "addItem";
      stepClientId: string;
      itemClientId: string;
      draft: AddItemDraft;
      pendingTitle: string | null;
    }
  | { kind: "updateItem"; itemClientId: string; changes: UpdateGraphItemFields }
  | { kind: "deleteItem"; itemClientId: string }
  | {
      kind: "moveItem";
      itemClientId: string;
      toStepClientId: string;
      /** Local insert index in the target step; omitted = end. */
      toIndex?: number;
      /** PATH-WIDE position for the wire; omitted = end-of-step. */
      position?: number;
      keepEmptyStep?: boolean;
    }
  | {
      kind: "reorderWithinStep";
      stepClientId: string;
      orderedItemClientIds: string[];
    }
  | {
      kind: "addConnection";
      connectionClientId: string;
      fromStepClientId: string;
      toStepClientId: string;
      type: GraphAuthorableConnectionType | "conditional";
      roleTag?: GraphRoleTag;
      tradeTag?: GraphTradeTag;
      conditionSourceItemClientId?: string;
      conditionBranch?: GraphConditionBranch;
    }
  | {
      kind: "updateConnection";
      connectionClientId: string;
      changes: UpdateGraphConnectionFields;
    }
  | { kind: "deleteConnection"; connectionClientId: string }
  | {
      kind: "createStepWithItem";
      stepClientId: string;
      itemClientId: string;
      label: string | null;
      requireAll: boolean;
      canvasX: number | null;
      canvasY: number | null;
      draft: AddItemDraft;
      pendingTitle: string | null;
    }
  /**
   * Several commands recorded as ONE history entry (recordAsOneEdit):
   * compound gestures like insert-on-edge or add-branch-path undo together.
   */
  | { kind: "batch"; commands: BuilderCommand[] }
  /** Inverse of a user deleteStep: rebuild the step, items and edges. */
  | { kind: "restoreStep"; snapshot: DeletedStepSnapshot };

/**
 * Everything needed to undo a step delete. The server re-chains the deleted
 * step's predecessors to its successors with NEW 'always' edges we only learn
 * about on the post-delete refetch, so the restore removes any 'always'
 * source→target edge that did not exist before the delete.
 */
export interface DeletedStepSnapshot {
  step: {
    clientId: string;
    label: string | null;
    partLabel: string | null;
    requireAll: boolean;
    unlockAfterDays: number | null;
    unlockMemoryKey: string | null;
    unlockMemoryRule: string | null;
    canvasX: number | null;
    canvasY: number | null;
  };
  items: Array<{
    itemClientId: string;
    draft: AddItemDraft;
    pendingTitle: string | null;
  }>;
  connections: Array<{
    connectionClientId: string;
    fromStepClientId: string;
    toStepClientId: string;
    type: GraphAuthorableConnectionType;
    roleTag?: GraphRoleTag;
    tradeTag?: GraphTradeTag;
  }>;
  rechainSources: string[];
  rechainTargets: string[];
  /** "from>to" keys of every 'always' edge before the delete. */
  alwaysBefore: string[];
}

export interface HistoryEntry {
  /** Inverse command, captured at dispatch time. */
  undo: BuilderCommand;
  /** The original forward command (replayed on redo). */
  redo: BuilderCommand;
}

/** Command history depth (builder design §5). */
export const HISTORY_DEPTH = 50;

export interface BuilderHistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
}

/**
 * clientId → serverId memory. Unlike the live entity maps, pairings SURVIVE
 * optimistic removal, so a queued delete enqueued before (or because of) an
 * undo can resolve its serverId at execution time. Monotone; cleared only
 * by resetBuilder.
 */
export interface IdPairings {
  steps: Record<string, number>;
  items: Record<string, number>;
  connections: Record<string, number>;
}

export interface BuilderQueueState {
  ops: QueueOp[];
  /** True while the head op's request is in flight. */
  running: boolean;
  /** True during failure handling (refetch in progress). */
  paused: boolean;
}

export interface PathBuilderGraphState {
  pathId: number | null;
  loadStatus: GraphLoadStatus;
  loadError: string | null;
  meta: BuilderMeta;
  steps: Record<string, BuilderStep>;
  connections: Record<string, BuilderConnection>;
  items: Record<string, BuilderItem>;
  selection: BuilderSelection;
  save: BuilderSaveState;
  queue: BuilderQueueState;
  pairings: IdPairings;
  history: BuilderHistoryState;
}

export interface PathBuilderGraphRootState {
  pathBuilderGraph: PathBuilderGraphState;
}

export type PathBuilderGraphThunk<R = void> = ThunkAction<
  R,
  PathBuilderGraphRootState,
  unknown,
  UnknownAction
>;

type GraphDispatch = ThunkDispatch<
  PathBuilderGraphRootState,
  unknown,
  UnknownAction
>;

const initialMeta: BuilderMeta = {
  name: "",
  description: null,
  companyId: null,
  status: "draft",
  isArchived: false,
  selfEnrollmentEnabled: false,
  growthStages: [],
  trades: [],
  roles: [],
  showInDirectory: true,
  estimatedMinutes: null,
  learningObjectives: [],
  thumbnailUrl: null,
  enforceOrder: true,
  serverUpdatedAt: "",
};

export const initialPathBuilderGraphState: PathBuilderGraphState = {
  pathId: null,
  loadStatus: "idle",
  loadError: null,
  meta: initialMeta,
  steps: {},
  connections: {},
  items: {},
  selection: { kind: null, id: null },
  save: { status: "idle", pendingOps: 0, lastSavedAt: null, lastError: null },
  queue: { ops: [], running: false, paused: false },
  pairings: { steps: {}, items: {}, connections: {} },
  history: { past: [], future: [] },
};

// --- Slice ---------------------------------------------------------------------

interface CandidateClientIds {
  steps: Record<number, string>;
  items: Record<number, string>;
  connections: Record<number, string>;
}

/**
 * Re-applies one queued op's optimistic effect onto freshly normalized
 * entity maps (graphLoaded). `prior` is the pre-normalization state — the
 * source of optimistic locals for creates. Mutation order follows queue
 * FIFO order, so creating ops run before ops that reference them.
 */
const replayQueuedOp = (
  op: QueueOp,
  prior: {
    steps: Record<string, BuilderStep>;
    items: Record<string, BuilderItem>;
  },
  steps: Record<string, BuilderStep>,
  items: Record<string, BuilderItem>,
  connections: Record<string, BuilderConnection>,
): void => {
  switch (op.kind) {
    case "createStep": {
      const local = prior.steps[op.stepClientId];
      if (
        local !== undefined &&
        local.serverId === null &&
        steps[op.stepClientId] === undefined
      ) {
        steps[op.stepClientId] = { ...local, itemIds: [] };
      }
      return;
    }
    case "updateStep": {
      const target = steps[op.stepClientId];
      if (target === undefined) {
        return;
      }
      if (op.changes.label !== undefined) {
        target.label = op.changes.label;
      }
      if (op.changes.partLabel !== undefined) {
        target.partLabel = op.changes.partLabel;
      }
      if (op.changes.requireAll !== undefined) {
        target.requireAll = op.changes.requireAll;
      }
      if (op.changes.unlockAfterDays !== undefined) {
        target.unlockAfterDays = op.changes.unlockAfterDays;
      }
      if (op.changes.unlockMemoryKey !== undefined) {
        target.unlockMemoryKey = op.changes.unlockMemoryKey;
      }
      if (op.changes.unlockMemoryRule !== undefined) {
        target.unlockMemoryRule = op.changes.unlockMemoryRule;
      }
      if (op.changes.canvasX !== undefined) {
        target.canvasX = op.changes.canvasX;
      }
      if (op.changes.canvasY !== undefined) {
        target.canvasY = op.changes.canvasY;
      }
      return;
    }
    case "updateStepPositions": {
      op.positions.forEach((position) => {
        const target = steps[position.stepClientId];
        if (target !== undefined) {
          target.canvasX = position.canvasX;
          target.canvasY = position.canvasY;
        }
      });
      return;
    }
    case "deleteStep": {
      const target = steps[op.stepClientId];
      if (target === undefined) {
        return;
      }
      target.itemIds.forEach((itemId) => {
        delete items[itemId];
      });
      delete steps[op.stepClientId];
      Object.values(connections).forEach((connection) => {
        if (
          connection.fromStepId === op.stepClientId ||
          connection.toStepId === op.stepClientId
        ) {
          delete connections[connection.clientId];
        }
      });
      return;
    }
    case "addItem": {
      const local = prior.items[op.itemClientId];
      if (
        local !== undefined &&
        local.serverId === null &&
        items[op.itemClientId] === undefined &&
        steps[local.stepClientId] !== undefined
      ) {
        items[op.itemClientId] = { ...local };
        steps[local.stepClientId].itemIds.push(op.itemClientId);
      }
      return;
    }
    case "updateItem": {
      const target = items[op.itemClientId];
      if (target === undefined) {
        return;
      }
      if (target.payload !== null) {
        target.payload = {
          ...target.payload,
          ...op.changes,
        } as GraphStepItem;
      }
      return;
    }
    case "deleteItem": {
      const target = items[op.itemClientId];
      if (target === undefined) {
        return;
      }
      const parent = steps[target.stepClientId];
      if (parent !== undefined) {
        parent.itemIds = parent.itemIds.filter(
          (itemId) => itemId !== op.itemClientId,
        );
      }
      delete items[op.itemClientId];
      return;
    }
    case "moveItem": {
      const target = items[op.itemClientId];
      const toStep = steps[op.toStepClientId];
      if (target === undefined || toStep === undefined) {
        return;
      }
      const fromStep = steps[target.stepClientId];
      if (fromStep !== undefined) {
        fromStep.itemIds = fromStep.itemIds.filter(
          (itemId) => itemId !== op.itemClientId,
        );
      }
      if (!toStep.itemIds.includes(op.itemClientId)) {
        toStep.itemIds.push(op.itemClientId);
      }
      target.stepClientId = op.toStepClientId;
      return;
    }
    case "reorderWithinStep": {
      const target = steps[op.stepClientId];
      if (target === undefined) {
        return;
      }
      const currentSet = [...target.itemIds].sort();
      const orderedSet = [...op.orderedItemClientIds].sort();
      const sameSet =
        currentSet.length === orderedSet.length &&
        currentSet.every((itemId, index) => itemId === orderedSet[index]);
      if (sameSet) {
        target.itemIds = [...op.orderedItemClientIds];
      }
      return;
    }
    case "addConnection": {
      if (
        connections[op.connectionClientId] === undefined &&
        steps[op.fromStepClientId] !== undefined &&
        steps[op.toStepClientId] !== undefined
      ) {
        connections[op.connectionClientId] = {
          clientId: op.connectionClientId,
          serverId: null,
          fromStepId: op.fromStepClientId,
          toStepId: op.toStepClientId,
          type: op.type,
          roleTag: op.roleTag,
          tradeTag: op.tradeTag,
          conditionSourceItemId: op.conditionSourceItemClientId,
          conditionBranch: op.conditionBranch,
        };
      }
      return;
    }
    case "updateConnection": {
      const target = connections[op.connectionClientId];
      if (target === undefined) {
        return;
      }
      if (op.changes.type !== undefined) {
        target.type = op.changes.type;
      }
      if (op.changes.roleTag !== undefined) {
        target.roleTag = op.changes.roleTag ?? undefined;
      }
      if (op.changes.tradeTag !== undefined) {
        target.tradeTag = op.changes.tradeTag ?? undefined;
      }
      if (op.changes.conditionBranch !== undefined) {
        target.conditionBranch = op.changes.conditionBranch;
      }
      return;
    }
    case "deleteConnection": {
      delete connections[op.connectionClientId];
      return;
    }
  }
};

const pathBuilderGraphSlice = createSlice({
  name: "pathBuilderGraph",
  initialState: initialPathBuilderGraphState,
  reducers: {
    // -- load lifecycle ---------------------------------------------------------
    graphLoadStarted(state) {
      state.loadStatus = "loading";
      state.loadError = null;
      // Explicit (re)load: command history does not survive a load boundary.
      state.history = { past: [], future: [] };
    },
    graphLoadFailed(state, action: PayloadAction<{ message: string }>) {
      state.loadStatus = "error";
      state.loadError = action.payload.message;
    },
    /**
     * Optimistic path-name change for the header inline rename. Keeps the
     * builder's meta.name in sync with the rename PUT so the title updates
     * immediately; on save failure the caller re-dispatches this with the
     * previous name to revert.
     */
    pathNameChanged(state, action: PayloadAction<string>) {
      state.meta.name = action.payload;
    },
    /**
     * Optimistic path-level enforce-order toggle (builder settings). Kept in
     * sync with the updatePath PUT so structural locking flips immediately; on
     * save failure the caller re-dispatches with the previous value to revert.
     */
    enforceOrderChanged(state, action: PayloadAction<boolean>) {
      state.meta.enforceOrder = action.payload;
    },
    selfEnrollmentChanged(state, action: PayloadAction<boolean>) {
      state.meta.selfEnrollmentEnabled = action.payload;
    },
    audienceChanged(
      state,
      action: PayloadAction<{
        growthStages?: string[];
        trades?: string[];
        roles?: string[];
      }>,
    ) {
      const { growthStages, trades, roles } = action.payload;
      if (growthStages !== undefined) state.meta.growthStages = growthStages;
      if (trades !== undefined) state.meta.trades = trades;
      if (roles !== undefined) state.meta.roles = roles;
    },
    /**
     * Optimistic path-details change (details dialog): name, description,
     * audience and metadata in one shot. The caller re-dispatches the
     * previous values to revert on save failure.
     */
    pathDetailsChanged(
      state,
      action: PayloadAction<
        Partial<
          Pick<
            BuilderMeta,
            | "name"
            | "description"
            | "selfEnrollmentEnabled"
            | "growthStages"
            | "trades"
            | "roles"
            | "showInDirectory"
            | "estimatedMinutes"
            | "learningObjectives"
            | "thumbnailUrl"
          >
        >
      >,
    ) {
      Object.assign(state.meta, action.payload);
    },
    /**
     * Normalizes a GraphResponse into the slice. Reuses existing clientIds
     * for known serverIds (React Flow key stability across refetches) and
     * preserves optimistic entities whose creating ops are still queued
     * (delete-reconcile refetch). Failure refetches clear the queue first,
     * so optimistic entities are dropped there — truth restore.
     */
    graphLoaded: {
      prepare(data: GraphResponse["data"]) {
        const candidates: CandidateClientIds = {
          steps: {},
          items: {},
          connections: {},
        };
        data.steps.forEach((step) => {
          candidates.steps[step.id] = nanoid();
          step.items.forEach((item) => {
            candidates.items[item.id] = nanoid();
          });
        });
        data.connections.forEach((connection) => {
          candidates.connections[connection.id] = nanoid();
        });
        return { payload: { data, candidates } };
      },
      reducer(
        state,
        action: PayloadAction<{
          data: GraphResponse["data"];
          candidates: CandidateClientIds;
        }>,
      ) {
        const { data, candidates } = action.payload;

        // clientId reuse is seeded from the persistent pairing map FIRST
        // (it remembers optimistically-removed entities), then live
        // entities overlay it. Both agree where they overlap.
        const knownStepIds: Record<number, string> = {};
        Object.entries(state.pairings.steps).forEach(([clientId, serverId]) => {
          knownStepIds[serverId] = clientId;
        });
        Object.values(state.steps).forEach((step) => {
          if (step.serverId !== null) {
            knownStepIds[step.serverId] = step.clientId;
          }
        });
        const knownItemIds: Record<number, string> = {};
        Object.entries(state.pairings.items).forEach(([clientId, serverId]) => {
          knownItemIds[serverId] = clientId;
        });
        Object.values(state.items).forEach((item) => {
          if (item.serverId !== null) {
            knownItemIds[item.serverId] = item.clientId;
          }
        });
        const knownConnectionIds: Record<number, string> = {};
        Object.entries(state.pairings.connections).forEach(
          ([clientId, serverId]) => {
            knownConnectionIds[serverId] = clientId;
          },
        );
        Object.values(state.connections).forEach((connection) => {
          if (connection.serverId !== null) {
            knownConnectionIds[connection.serverId] = connection.clientId;
          }
        });

        state.pathId = data.path.id;
        state.loadStatus = "loaded";
        state.loadError = null;
        state.meta = {
          name: data.path.name,
          description: data.path.description,
          companyId: data.path.companyId,
          status: data.path.status,
          isArchived: data.path.isArchived,
          selfEnrollmentEnabled: data.path.selfEnrollmentEnabled,
          growthStages: data.path.growthStages ?? [],
          trades: data.path.trades ?? [],
          roles: data.path.roles ?? [],
          showInDirectory: data.path.showInDirectory ?? true,
          estimatedMinutes: data.path.estimatedMinutes ?? null,
          learningObjectives: data.path.learningObjectives ?? [],
          thumbnailUrl: data.path.thumbnailUrl ?? null,
          enforceOrder: data.path.enforceOrder,
          serverUpdatedAt: data.path.updatedAt,
        };

        const steps: Record<string, BuilderStep> = {};
        const items: Record<string, BuilderItem> = {};
        const connections: Record<string, BuilderConnection> = {};
        const stepClientByServer: Record<number, string> = {};
        const itemClientByServer: Record<number, string> = {};

        const orderedSteps = [...data.steps].sort(
          (a, b) => a.sortKey - b.sortKey || a.id - b.id,
        );
        orderedSteps.forEach((serverStep) => {
          const clientId =
            knownStepIds[serverStep.id] ?? candidates.steps[serverStep.id];
          stepClientByServer[serverStep.id] = clientId;
          state.pairings.steps[clientId] = serverStep.id;
          const itemIds: string[] = [];
          const orderedItems = [...serverStep.items].sort(
            (a, b) => a.position - b.position || a.id - b.id,
          );
          orderedItems.forEach((dto) => {
            const itemClientId =
              knownItemIds[dto.id] ?? candidates.items[dto.id];
            itemClientByServer[dto.id] = itemClientId;
            state.pairings.items[itemClientId] = dto.id;
            items[itemClientId] = {
              clientId: itemClientId,
              serverId: dto.id,
              stepClientId: clientId,
              type: dto.type,
              position: dto.position,
              pendingTitle: null,
              payload: dto,
            };
            itemIds.push(itemClientId);
          });
          steps[clientId] = {
            clientId,
            serverId: serverStep.id,
            label: serverStep.label,
            partLabel: serverStep.partLabel ?? null,
            requireAll: serverStep.requireAll,
            sortKey: serverStep.sortKey,
            unlockAfterDays: serverStep.unlockAfterDays ?? null,
            unlockMemoryKey: serverStep.unlockMemoryKey ?? null,
            unlockMemoryRule: serverStep.unlockMemoryRule ?? null,
            canvasX: serverStep.canvasX,
            canvasY: serverStep.canvasY,
            itemIds,
          };
        });

        data.connections.forEach((serverConnection) => {
          const fromClientId = stepClientByServer[serverConnection.fromStepId];
          const toClientId = stepClientByServer[serverConnection.toStepId];
          if (fromClientId === undefined || toClientId === undefined) {
            return; // defensive: dangling edge in the response
          }
          const clientId =
            knownConnectionIds[serverConnection.id] ??
            candidates.connections[serverConnection.id];
          state.pairings.connections[clientId] = serverConnection.id;
          connections[clientId] = {
            clientId,
            serverId: serverConnection.id,
            fromStepId: fromClientId,
            toStepId: toClientId,
            type: serverConnection.type,
            roleTag: serverConnection.roleTag ?? undefined,
            tradeTag: serverConnection.tradeTag ?? undefined,
            conditionBranch: serverConnection.conditionBranch ?? undefined,
            scoreThreshold: serverConnection.scoreThreshold ?? undefined,
            conditionSourceItemId:
              serverConnection.conditionSourceItemId === null
                ? undefined
                : itemClientByServer[serverConnection.conditionSourceItemId],
          };
        });

        // Replay every still-queued op's OPTIMISTIC effect onto the
        // normalized maps, in FIFO order. A mid-queue reconcile refetch
        // (post-deleteStep, post-move-that-emptied-a-step) must neither
        // resurrect optimistically-deleted entities nor drop optimistic
        // creates/edits whose ops have not executed yet. Failure refetches
        // clear the queue first, so this loop is a no-op there.
        state.queue.ops.forEach((op) => {
          replayQueuedOp(op, state, steps, items, connections);
        });

        state.steps = steps;
        state.items = items;
        state.connections = connections;

        const selectedId = state.selection.id;
        if (selectedId !== null) {
          const stillExists =
            (state.selection.kind === "step" &&
              steps[selectedId] !== undefined) ||
            (state.selection.kind === "item" &&
              items[selectedId] !== undefined) ||
            (state.selection.kind === "connection" &&
              connections[selectedId] !== undefined);
          if (!stillExists) {
            state.selection = { kind: null, id: null };
          }
        }
      },
    },
    builderStateReset() {
      return initialPathBuilderGraphState;
    },

    // -- selection ---------------------------------------------------------------
    selectionChanged(
      state,
      action: PayloadAction<{ kind: SelectionKind; id: string }>,
    ) {
      state.selection = action.payload;
    },
    selectionCleared(state) {
      state.selection = { kind: null, id: null };
    },

    // -- optimistic entity mutations (internal; use the enqueue* thunks) ---------
    stepAddedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        label: string | null;
        requireAll: boolean;
        canvasX: number | null;
        canvasY: number | null;
      }>,
    ) {
      const { clientId, label, requireAll, canvasX, canvasY } = action.payload;
      const maxSortKey = Object.values(state.steps).reduce(
        (max, step) => Math.max(max, step.sortKey),
        -1,
      );
      state.steps[clientId] = {
        clientId,
        serverId: null,
        label,
        partLabel: null,
        requireAll,
        sortKey: maxSortKey + 1,
        unlockAfterDays: null,
        unlockMemoryKey: null,
        unlockMemoryRule: null,
        canvasX,
        canvasY,
        itemIds: [],
      };
    },
    stepPatchedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        changes: {
          label?: string | null;
          partLabel?: string | null;
          requireAll?: boolean;
          unlockAfterDays?: number | null;
          unlockMemoryKey?: string | null;
          unlockMemoryRule?: string | null;
          canvasX?: number | null;
          canvasY?: number | null;
        };
      }>,
    ) {
      const step = state.steps[action.payload.clientId];
      if (step === undefined) {
        return;
      }
      const { changes } = action.payload;
      if (changes.label !== undefined) {
        step.label = changes.label;
      }
      if (changes.partLabel !== undefined) {
        step.partLabel = changes.partLabel;
      }
      if (changes.requireAll !== undefined) {
        step.requireAll = changes.requireAll;
      }
      if (changes.unlockAfterDays !== undefined) {
        step.unlockAfterDays = changes.unlockAfterDays;
      }
      if (changes.unlockMemoryKey !== undefined) {
        step.unlockMemoryKey = changes.unlockMemoryKey;
      }
      if (changes.unlockMemoryRule !== undefined) {
        step.unlockMemoryRule = changes.unlockMemoryRule;
      }
      if (changes.canvasX !== undefined) {
        step.canvasX = changes.canvasX;
      }
      if (changes.canvasY !== undefined) {
        step.canvasY = changes.canvasY;
      }
    },
    stepPositionsPatchedLocally(
      state,
      action: PayloadAction<{
        positions: Array<{
          clientId: string;
          canvasX: number | null;
          canvasY: number | null;
        }>;
      }>,
    ) {
      action.payload.positions.forEach((position) => {
        const step = state.steps[position.clientId];
        if (step === undefined) {
          return;
        }
        step.canvasX = position.canvasX;
        step.canvasY = position.canvasY;
      });
    },
    stepRemovedLocally(state, action: PayloadAction<{ clientId: string }>) {
      const step = state.steps[action.payload.clientId];
      if (step === undefined) {
        return;
      }
      step.itemIds.forEach((itemId) => {
        delete state.items[itemId];
      });
      delete state.steps[action.payload.clientId];
      Object.values(state.connections).forEach((connection) => {
        if (
          connection.fromStepId === action.payload.clientId ||
          connection.toStepId === action.payload.clientId
        ) {
          delete state.connections[connection.clientId];
        }
      });
      const selectedId = state.selection.id;
      if (
        selectedId !== null &&
        state.steps[selectedId] === undefined &&
        state.items[selectedId] === undefined &&
        state.connections[selectedId] === undefined
      ) {
        state.selection = { kind: null, id: null };
      }
    },
    itemAddedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        stepClientId: string;
        type: GraphItemType;
        position?: number;
        pendingTitle: string | null;
      }>,
    ) {
      const { clientId, stepClientId, type, position, pendingTitle } =
        action.payload;
      const step = state.steps[stepClientId];
      if (step === undefined) {
        return;
      }
      state.items[clientId] = {
        clientId,
        serverId: null,
        stepClientId,
        type,
        // Placeholder until itemConfirmed writes the server's path-wide
        // position; itemIds order is the display truth meanwhile.
        position: position ?? step.itemIds.length,
        pendingTitle,
        payload: null,
      };
      step.itemIds.push(clientId);
    },
    itemPatchedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        changes: UpdateGraphItemFields;
      }>,
    ) {
      const item = state.items[action.payload.clientId];
      if (item === undefined) {
        return;
      }
      const { changes } = action.payload;
      if (item.payload !== null) {
        item.payload = { ...item.payload, ...changes } as GraphStepItem;
        return;
      }
      // Creation still in flight: keep the optimistic chip title fresh.
      if (typeof changes.taskName === "string") {
        item.pendingTitle = changes.taskName;
      } else if (typeof changes.milestoneName === "string") {
        item.pendingTitle = changes.milestoneName;
      }
    },
    itemRemovedLocally(state, action: PayloadAction<{ clientId: string }>) {
      const item = state.items[action.payload.clientId];
      if (item === undefined) {
        return;
      }
      const step = state.steps[item.stepClientId];
      if (step !== undefined) {
        step.itemIds = step.itemIds.filter(
          (itemId) => itemId !== action.payload.clientId,
        );
      }
      delete state.items[action.payload.clientId];
      if (
        state.selection.kind === "item" &&
        state.selection.id === action.payload.clientId
      ) {
        state.selection = { kind: null, id: null };
      }
    },
    itemMovedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        toStepClientId: string;
        toIndex?: number;
      }>,
    ) {
      const { clientId, toStepClientId, toIndex } = action.payload;
      const item = state.items[clientId];
      const toStep = state.steps[toStepClientId];
      if (item === undefined || toStep === undefined) {
        return;
      }
      const fromStep = state.steps[item.stepClientId];
      if (fromStep !== undefined) {
        fromStep.itemIds = fromStep.itemIds.filter(
          (itemId) => itemId !== clientId,
        );
      }
      const insertAt =
        toIndex === undefined
          ? toStep.itemIds.length
          : Math.max(0, Math.min(toIndex, toStep.itemIds.length));
      toStep.itemIds.splice(insertAt, 0, clientId);
      item.stepClientId = toStepClientId;
    },
    stepItemsReorderedLocally(
      state,
      action: PayloadAction<{
        stepClientId: string;
        orderedItemClientIds: string[];
      }>,
    ) {
      const step = state.steps[action.payload.stepClientId];
      if (step === undefined) {
        return;
      }
      step.itemIds = [...action.payload.orderedItemClientIds];
    },
    connectionAddedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        fromStepClientId: string;
        toStepClientId: string;
        type: GraphAuthorableConnectionType | "conditional";
        roleTag?: GraphRoleTag;
        tradeTag?: GraphTradeTag;
        conditionSourceItemClientId?: string;
        conditionBranch?: GraphConditionBranch;
      }>,
    ) {
      const { clientId, fromStepClientId, toStepClientId, type } =
        action.payload;
      if (
        state.steps[fromStepClientId] === undefined ||
        state.steps[toStepClientId] === undefined
      ) {
        return;
      }
      state.connections[clientId] = {
        clientId,
        serverId: null,
        fromStepId: fromStepClientId,
        toStepId: toStepClientId,
        type,
        roleTag: action.payload.roleTag,
        tradeTag: action.payload.tradeTag,
        conditionSourceItemId: action.payload.conditionSourceItemClientId,
        conditionBranch: action.payload.conditionBranch,
      };
    },
    connectionPatchedLocally(
      state,
      action: PayloadAction<{
        clientId: string;
        changes: UpdateGraphConnectionFields;
      }>,
    ) {
      const connection = state.connections[action.payload.clientId];
      if (connection === undefined) {
        return;
      }
      const { changes } = action.payload;
      if (changes.type !== undefined) {
        connection.type = changes.type;
        // Retyping clears the now-inapplicable predicate tag locally; the
        // confirmed server connection is authoritative either way.
        if (changes.type !== "role-based" && changes.roleTag === undefined) {
          connection.roleTag = undefined;
        }
        if (changes.type !== "trade-type" && changes.tradeTag === undefined) {
          connection.tradeTag = undefined;
        }
      }
      if (changes.roleTag !== undefined) {
        connection.roleTag = changes.roleTag ?? undefined;
      }
      if (changes.tradeTag !== undefined) {
        connection.tradeTag = changes.tradeTag ?? undefined;
      }
      if (changes.conditionBranch !== undefined) {
        connection.conditionBranch = changes.conditionBranch;
      }
    },
    connectionRemovedLocally(
      state,
      action: PayloadAction<{ clientId: string }>,
    ) {
      if (state.connections[action.payload.clientId] === undefined) {
        return;
      }
      delete state.connections[action.payload.clientId];
      if (
        state.selection.kind === "connection" &&
        state.selection.id === action.payload.clientId
      ) {
        state.selection = { kind: null, id: null };
      }
    },

    // -- server confirmations (internal) ------------------------------------------
    serverUpdatedAtReceived(state, action: PayloadAction<string>) {
      state.meta.serverUpdatedAt = action.payload;
    },
    /**
     * Pairs serverId onto the step and applies server-authoritative fields.
     * canvasX/canvasY and itemIds are intentionally NOT overwritten: local
     * values are at least as fresh (the response echoes the creation payload;
     * any newer drag has a queued positions op behind this one).
     */
    stepConfirmed(
      state,
      action: PayloadAction<{
        clientId: string;
        step: GraphStep;
        updatedAt: string;
      }>,
    ) {
      state.meta.serverUpdatedAt = action.payload.updatedAt;
      // Pairing is recorded even when the entity was optimistically removed
      // (undo enqueued a delete behind this create) — the late-bound delete
      // resolves its serverId here.
      state.pairings.steps[action.payload.clientId] = action.payload.step.id;
      const step = state.steps[action.payload.clientId];
      if (step === undefined) {
        return;
      }
      step.serverId = action.payload.step.id;
      step.label = action.payload.step.label;
      step.partLabel = action.payload.step.partLabel ?? null;
      step.requireAll = action.payload.step.requireAll;
      step.sortKey = action.payload.step.sortKey;
      step.unlockAfterDays = action.payload.step.unlockAfterDays ?? null;
      step.unlockMemoryKey = action.payload.step.unlockMemoryKey ?? null;
      step.unlockMemoryRule = action.payload.step.unlockMemoryRule ?? null;
    },
    itemConfirmed(
      state,
      action: PayloadAction<{
        clientId: string;
        item: GraphStepItem;
        updatedAt: string;
      }>,
    ) {
      state.meta.serverUpdatedAt = action.payload.updatedAt;
      state.pairings.items[action.payload.clientId] = action.payload.item.id;
      const item = state.items[action.payload.clientId];
      if (item === undefined) {
        return;
      }
      item.serverId = action.payload.item.id;
      item.type = action.payload.item.type;
      item.position = action.payload.item.position;
      item.payload = action.payload.item;
      item.pendingTitle = null;
    },
    /**
     * Pairs serverId onto the connection and applies server-authoritative
     * predicate fields. from/to stay as local step clientIds (the server
     * echoes the ids we sent).
     */
    connectionConfirmed(
      state,
      action: PayloadAction<{
        clientId: string;
        connection: GraphConnection;
        updatedAt: string;
      }>,
    ) {
      state.meta.serverUpdatedAt = action.payload.updatedAt;
      state.pairings.connections[action.payload.clientId] =
        action.payload.connection.id;
      const connection = state.connections[action.payload.clientId];
      if (connection === undefined) {
        return;
      }
      const dto = action.payload.connection;
      connection.serverId = dto.id;
      connection.type = dto.type;
      connection.roleTag = dto.roleTag ?? undefined;
      connection.tradeTag = dto.tradeTag ?? undefined;
      connection.conditionBranch = dto.conditionBranch ?? undefined;
      connection.scoreThreshold = dto.scoreThreshold ?? undefined;
    },

    // -- undo/redo history (internal; use undoGraphEdit/redoGraphEdit) -----------
    historyRecorded(state, action: PayloadAction<{ entry: HistoryEntry }>) {
      state.history.past.push(action.payload.entry);
      if (state.history.past.length > HISTORY_DEPTH) {
        state.history.past.shift();
      }
      state.history.future = [];
    },
    historyCleared(state) {
      state.history = { past: [], future: [] };
    },
    historyUndone(state) {
      const entry = state.history.past.pop();
      if (entry !== undefined) {
        state.history.future.push(entry);
      }
    },
    historyRedone(state) {
      const entry = state.history.future.pop();
      if (entry !== undefined) {
        state.history.past.push(entry);
      }
    },

    // -- queue bookkeeping (internal) ----------------------------------------------
    opEnqueued(state, action: PayloadAction<QueueOp>) {
      state.queue.ops.push(action.payload);
      state.save.pendingOps = state.queue.ops.length;
      if (state.save.status !== "saving") {
        state.save.status = "saving";
      }
    },
    opStarted(state) {
      state.queue.running = true;
      state.save.status = "saving";
    },
    opSucceeded: {
      prepare() {
        return { payload: { savedAt: new Date().toISOString() } };
      },
      reducer(state, action: PayloadAction<{ savedAt: string }>) {
        state.queue.ops.shift();
        state.queue.running = false;
        state.save.pendingOps = state.queue.ops.length;
        state.save.lastSavedAt = action.payload.savedAt;
        state.save.lastError = null;
        state.save.status = state.queue.ops.length === 0 ? "saved" : "saving";
      },
    },
    /**
     * Drops ALL pending ops and pauses the queue (failure path). The
     * command history goes with them — after the truth-restore refetch,
     * recorded inverses may no longer correspond to server state.
     */
    queueFailed(state, action: PayloadAction<{ message: string }>) {
      state.queue.ops = [];
      state.queue.running = false;
      state.queue.paused = true;
      state.save.pendingOps = 0;
      state.save.status = "error";
      state.save.lastError = action.payload.message;
      state.history = { past: [], future: [] };
    },
    queueResumed(state) {
      state.queue.paused = false;
    },
  },
  extraReducers: (builder) => {
    // Path-level PUTs (header rename, enforce-order toggle) bump the server's
    // updated_at too. Without this the next graph mutation sends a stale
    // expectedUpdatedAt, 409s, and the queue drops the user's edit.
    builder.addMatcher(
      (action): action is PayloadAction<LearningPathResponse> =>
        action.type === UPDATE_PATH_FULFILLED,
      (state, action) => {
        const { path } = action.payload;
        if (path.id === state.pathId) {
          state.meta.serverUpdatedAt = path.updatedAt;
        }
      },
    );
  },
});

const { actions } = pathBuilderGraphSlice;

/** Public reducer-actions. Everything else flows through the thunks below. */
export const {
  graphLoaded,
  pathNameChanged,
  enforceOrderChanged,
  selfEnrollmentChanged,
  audienceChanged,
  pathDetailsChanged,
  selectionChanged,
  selectionCleared,
} = actions;

/** Internal actions exposed for unit tests only. */
export const pathBuilderGraphInternalActions = actions;

// --- Error helpers ---------------------------------------------------------------

const getHttpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * Staleness is 409 + the backend's literal {error:'stale'} body — a plain
 * status check conflated duplicate-course 409s with concurrent-edit 409s and
 * showed the misleading "changed elsewhere" toast (M3 review must-fix #2).
 */
const isStaleConflict = (error: unknown): boolean => {
  if (getHttpStatus(error) !== 409) {
    return false;
  }
  const body = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data;
  return body?.error === "stale";
};

const extractErrorMessage = (error: unknown): string => {
  const responseData = (
    error as { response?: { data?: { error?: unknown; message?: unknown } } }
  )?.response?.data;
  if (
    typeof responseData?.error === "string" &&
    responseData.error !== "stale"
  ) {
    return responseData.error;
  }
  if (typeof responseData?.message === "string") {
    return responseData.message;
  }
  if (error instanceof Error && error.message !== "") {
    return error.message;
  }
  return "Something went wrong while saving the path.";
};

const STALE_RELOADED_MESSAGE =
  "This path was changed elsewhere — reloaded with the latest version.";

// --- Queue machinery ----------------------------------------------------------------

const assertPathLoaded = (state: PathBuilderGraphRootState): void => {
  if (state.pathBuilderGraph.pathId === null) {
    throw new Error(
      "pathBuilderGraph: no path loaded — dispatch loadPathGraph first",
    );
  }
};

/**
 * serverId resolution at op-execution time: live entity first, then the
 * persistent pairing map (covers entities optimistically removed by a
 * queued delete). FIFO ordering guarantees the creating op confirmed first.
 */
const resolveStepServerId = (
  state: PathBuilderGraphState,
  stepClientId: string,
): number | null =>
  state.steps[stepClientId]?.serverId ??
  state.pairings.steps[stepClientId] ??
  null;

const requireStepServerId = (
  state: PathBuilderGraphState,
  stepClientId: string,
): number => {
  const serverId = resolveStepServerId(state, stepClientId);
  if (serverId === null) {
    throw new Error(
      "pathBuilderGraph: step has no server id (creating op unresolved)",
    );
  }
  return serverId;
};

const requireItemServerId = (
  state: PathBuilderGraphState,
  itemClientId: string,
): number => {
  const serverId =
    state.items[itemClientId]?.serverId ??
    state.pairings.items[itemClientId] ??
    null;
  if (serverId === null) {
    throw new Error(
      "pathBuilderGraph: item has no server id (creating op unresolved)",
    );
  }
  return serverId;
};

const requireConnectionServerId = (
  state: PathBuilderGraphState,
  connectionClientId: string,
): number => {
  const serverId =
    state.connections[connectionClientId]?.serverId ??
    state.pairings.connections[connectionClientId] ??
    null;
  if (serverId === null) {
    throw new Error(
      "pathBuilderGraph: connection has no server id (creating op unresolved)",
    );
  }
  return serverId;
};

async function executeOp(
  op: QueueOp,
  dispatch: GraphDispatch,
  getState: () => PathBuilderGraphRootState,
): Promise<void> {
  const current = getState().pathBuilderGraph;
  const pathId = current.pathId;
  if (pathId === null) {
    throw new Error("pathBuilderGraph: no path loaded");
  }
  const expectedUpdatedAt = current.meta.serverUpdatedAt;

  switch (op.kind) {
    case "createStep": {
      const response = await createStep(pathId, {
        label: op.label,
        requireAll: op.requireAll,
        canvasX: op.canvasX,
        canvasY: op.canvasY,
        expectedUpdatedAt,
      });
      dispatch(
        actions.stepConfirmed({
          clientId: op.stepClientId,
          step: response.data.step,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "updateStep": {
      const stepId = requireStepServerId(current, op.stepClientId);
      const { unlockMemoryKey, unlockMemoryRule, ...rest } = op.changes;
      const response = await updateStep(pathId, stepId, {
        ...rest,
        // PATCH: null = unchanged server-side, so a cleared gate goes as "".
        ...(unlockMemoryKey !== undefined
          ? { unlockMemoryKey: unlockMemoryKey ?? "" }
          : {}),
        ...(unlockMemoryRule !== undefined
          ? { unlockMemoryRule: unlockMemoryRule ?? "" }
          : {}),
        expectedUpdatedAt,
      });
      dispatch(
        actions.stepConfirmed({
          clientId: op.stepClientId,
          step: response.data.step,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "updateStepPositions": {
      const positions: StepPositionInput[] = [];
      op.positions.forEach((position) => {
        // Steps deleted since the drag need no position update; skip them.
        const serverId = current.steps[position.stepClientId]?.serverId ?? null;
        if (serverId !== null) {
          positions.push({
            stepId: serverId,
            canvasX: position.canvasX,
            canvasY: position.canvasY,
          });
        }
      });
      if (positions.length === 0) {
        return;
      }
      const response = await updateStepPositions(pathId, {
        positions,
        expectedUpdatedAt,
      });
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      return;
    }
    case "deleteStep": {
      const stepServerId =
        op.stepServerId ?? resolveStepServerId(current, op.stepClientId);
      if (stepServerId === null) {
        throw new Error(
          "pathBuilderGraph: step has no server id (creating op unresolved)",
        );
      }
      const response = await deleteStep(pathId, stepServerId, {
        expectedUpdatedAt,
      });
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      // The backend re-chained predecessor→successor 'always' edges with ids
      // we do not know; reconcile via refetch (clientIds stay stable).
      const graph = await getPathGraph(pathId);
      dispatch(actions.graphLoaded(graph.data));
      return;
    }
    case "addItem": {
      const stepId = requireStepServerId(current, op.stepClientId);
      const request = {
        ...op.draft,
        expectedUpdatedAt,
      } as AddItemToStepRequest;
      const response = await addItemToStep(pathId, stepId, request);
      dispatch(
        actions.itemConfirmed({
          clientId: op.itemClientId,
          item: response.data.item,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "updateItem": {
      const itemId = requireItemServerId(current, op.itemClientId);
      const response = await updateGraphItem(pathId, itemId, {
        ...op.changes,
        expectedUpdatedAt,
      });
      dispatch(
        actions.itemConfirmed({
          clientId: op.itemClientId,
          item: response.data.item,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "deleteItem": {
      const itemId = requireItemServerId(current, op.itemClientId);
      const response = await deleteGraphItem(pathId, itemId, {
        expectedUpdatedAt,
      });
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      // Deleting the step's last item also deletes the (re-chained) step
      // server-side — reconcile so no ghost step lingers client-side (M4
      // review blocker #1; mirrors the moveItem reconcile). typeof guard:
      // tolerate envelopes without the field (older mocks/responses).
      if (typeof response.data.deletedStepId === "number") {
        const graph = await getPathGraph(pathId);
        dispatch(actions.graphLoaded(graph.data));
      }
      return;
    }
    case "moveItem": {
      const itemId = requireItemServerId(current, op.itemClientId);
      const toStepId = requireStepServerId(current, op.toStepClientId);
      const request: Parameters<typeof moveGraphItem>[2] = {
        toStepId,
        expectedUpdatedAt,
      };
      if (op.position !== undefined) {
        request.position = op.position;
      }
      if (op.keepEmptyStep !== undefined) {
        request.keepEmptyStep = op.keepEmptyStep;
      }
      const response = await moveGraphItem(pathId, itemId, request);
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      // The server deletes the emptied source step and re-chains its edges
      // (ids unknown to us) unless keepEmptyStep was passed — reconcile.
      const fresh = getState().pathBuilderGraph;
      const sourceEmptied =
        (fresh.steps[op.fromStepClientId]?.itemIds.length ?? 0) === 0;
      if (sourceEmptied && op.keepEmptyStep !== true) {
        const graph = await getPathGraph(pathId);
        dispatch(actions.graphLoaded(graph.data));
      }
      return;
    }
    case "reorderWithinStep": {
      const stepId = requireStepServerId(current, op.stepClientId);
      // COMPLETE permutation: the step's current path-wide positions are
      // reassigned (sorted ascending) across the new display order.
      const positions = op.orderedItemClientIds
        .map((itemClientId) => {
          const item = current.items[itemClientId];
          if (item === undefined) {
            throw new Error(
              `pathBuilderGraph: unknown item ${itemClientId} in reorder`,
            );
          }
          return item.position;
        })
        .sort((a, b) => a - b);
      const items = op.orderedItemClientIds.map((itemClientId, index) => ({
        itemId: requireItemServerId(current, itemClientId),
        position: positions[index],
      }));
      const response = await reorderStepItems(pathId, stepId, {
        items,
        expectedUpdatedAt,
      });
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      return;
    }
    case "addConnection": {
      const request: Parameters<typeof createGraphConnection>[1] = {
        fromStepId: requireStepServerId(current, op.fromStepClientId),
        toStepId: requireStepServerId(current, op.toStepClientId),
        type: op.type,
        expectedUpdatedAt,
      };
      if (op.roleTag !== undefined) {
        request.roleTag = op.roleTag;
      }
      if (op.tradeTag !== undefined) {
        request.tradeTag = op.tradeTag;
      }
      if (op.conditionSourceItemClientId !== undefined) {
        request.conditionSourceItemId = requireItemServerId(
          current,
          op.conditionSourceItemClientId,
        );
      }
      if (op.conditionBranch !== undefined) {
        request.conditionBranch = op.conditionBranch;
      }
      const response = await createGraphConnection(pathId, request);
      dispatch(
        actions.connectionConfirmed({
          clientId: op.connectionClientId,
          connection: response.data.connection,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "updateConnection": {
      const connectionId = requireConnectionServerId(
        current,
        op.connectionClientId,
      );
      const response = await updateGraphConnection(pathId, connectionId, {
        ...op.changes,
        expectedUpdatedAt,
      });
      dispatch(
        actions.connectionConfirmed({
          clientId: op.connectionClientId,
          connection: response.data.connection,
          updatedAt: response.data.path.updatedAt,
        }),
      );
      return;
    }
    case "deleteConnection": {
      const connectionId = requireConnectionServerId(
        current,
        op.connectionClientId,
      );
      const response = await deleteGraphConnection(pathId, connectionId, {
        expectedUpdatedAt,
      });
      dispatch(actions.serverUpdatedAtReceived(response.data.path.updatedAt));
      return;
    }
  }
}

/**
 * Refetches GET /graph and replaces local state with server truth, without
 * the loading-status churn of loadPathGraph (used by the failure path).
 */
const refetchGraphTruth =
  (): PathBuilderGraphThunk<Promise<void>> => async (dispatch, getState) => {
    const pathId = getState().pathBuilderGraph.pathId;
    if (pathId === null) {
      return;
    }
    try {
      const response = await getPathGraph(pathId);
      dispatch(actions.graphLoaded(response.data));
    } catch (error) {
      dispatch(
        actions.graphLoadFailed({ message: extractErrorMessage(error) }),
      );
    }
  };

/**
 * Failure path for ANY queue op (409 stale included): pause the queue, drop
 * all pending ops, toast, refetch truth, resume with an empty queue.
 */
const handleQueueFailure =
  (error: unknown): PathBuilderGraphThunk<Promise<void>> =>
  async (dispatch) => {
    const stale = isStaleConflict(error);
    const message = stale ? STALE_RELOADED_MESSAGE : extractErrorMessage(error);
    dispatch(actions.queueFailed({ message }));
    toast({
      title: stale ? "Path reloaded" : "Save failed",
      description: message,
      variant: "destructive",
    });
    await dispatch(refetchGraphTruth());
    dispatch(actions.queueResumed());
    // Ops enqueued while recovery was in progress start here.
    void dispatch(pumpQueue());
  };

/**
 * Serial FIFO pump: at most one op in flight, ever. Ops referencing an
 * entity whose serverId is still null implicitly block — FIFO order means
 * the creating op resolves first, and serverIds are resolved from fresh
 * state at execution time.
 */
const pumpQueue =
  (): PathBuilderGraphThunk<Promise<void>> => async (dispatch, getState) => {
    for (;;) {
      const state = getState().pathBuilderGraph;
      if (state.queue.running || state.queue.paused) {
        return;
      }
      const op = state.queue.ops[0];
      if (op === undefined) {
        return;
      }
      dispatch(actions.opStarted());
      try {
        await executeOp(op, dispatch, getState);
        dispatch(actions.opSucceeded());
      } catch (error) {
        await dispatch(handleQueueFailure(error));
        return;
      }
    }
  };

// --- Command layer (undo/redo over the queue) ---------------------------------------

/** Drops keys whose value is undefined (clean Redux state + clean wire payloads). */
const stripUndefined = <T extends object>(value: T): T => {
  const result = { ...value } as Record<string, unknown>;
  Object.keys(result).forEach((key) => {
    if (result[key] === undefined) {
      delete result[key];
    }
  });
  return result as T;
};

/** Optimistic chip title for an add draft (server DTO replaces it on confirm). */
const derivePendingTitle = (draft: AddItemDraft): string | null => {
  switch (draft.type) {
    case "milestone":
      return draft.milestoneName;
    case "checklist_task":
    case "meeting_task":
    case "event_task":
    case "video":
    case "image":
    case "document_upload":
    case "text_block":
      return draft.taskName;
    default:
      // course renders its courseName from the payload; custom_task titles
      // live on the template — callers pass pendingTitle explicitly.
      return null;
  }
};

/** Maps server checklist rows back to the request input shape. */
const checklistRowsToInputs = (
  rows: unknown,
): Array<{
  name: string;
  description?: string;
  ownerId?: number;
  dueDate?: string;
}> => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => {
    const config = row as {
      name?: string;
      description?: string | null;
      ownerUserId?: number | null;
      dueDate?: string | null;
    };
    return stripUndefined({
      name: config.name ?? "",
      description: config.description ?? undefined,
      ownerId: config.ownerUserId ?? undefined,
      dueDate: config.dueDate ?? undefined,
    });
  });
};

/**
 * Previous values for exactly the keys a partial item update touches —
 * captured AT DISPATCH TIME from the current server payload (patch-with-prev
 * inverse). Missing keys invert to null (clear).
 */
const captureItemPrev = (
  payload: GraphStepItem,
  changes: UpdateGraphItemFields,
): UpdateGraphItemFields => {
  const source = payload as unknown as Record<string, unknown>;
  const prev: Record<string, unknown> = {};
  Object.keys(changes).forEach((key) => {
    if (key === "checklistItems") {
      prev.checklistItems = checklistRowsToInputs(source.checklistItems);
      return;
    }
    const value = source[key];
    prev[key] = value === undefined ? null : value;
  });
  return prev as UpdateGraphItemFields;
};

/**
 * Rebuilds the add-item draft for a delete's inverse from the item's server
 * payload (or, for an unconfirmed item, from its still-queued create op).
 * Returns null when reconstruction is impossible (unmodeled legacy types) —
 * the delete is then simply not recorded in history.
 */
const reconstructAddDraft = (
  item: BuilderItem,
  queuedDraft: AddItemDraft | undefined,
): AddItemDraft | null => {
  const payload = item.payload;
  if (payload === null) {
    return queuedDraft ?? null;
  }
  switch (payload.type) {
    case "course":
      return {
        type: "course",
        courseId: payload.courseId,
        position: payload.position,
      };
    case "milestone":
      return stripUndefined<Extract<AddItemDraft, { type: "milestone" }>>({
        type: "milestone",
        milestoneName: payload.milestoneName,
        milestoneDescription: payload.milestoneDescription ?? undefined,
        milestoneIcon: payload.milestoneIcon,
        // Badge GUIDs (carry-over #8 re-key). String() also covers stale
        // numeric payloads from the apiTypes shape during the cutover.
        badgeId:
          payload.badgeId !== null && payload.badgeId !== undefined
            ? String(payload.badgeId)
            : undefined,
        pointsAwarded: payload.pointsAwarded,
        certificateId: payload.certificateId ?? undefined,
        celebration: payload.celebration ?? undefined,
        position: payload.position,
      });
    case "checklist_task":
      return stripUndefined<Extract<AddItemDraft, { type: "checklist_task" }>>({
        type: "checklist_task",
        taskName: payload.taskName,
        taskDescription: payload.taskDescription ?? undefined,
        taskDueDate: payload.taskDueDate ?? undefined,
        checklistItems: checklistRowsToInputs(payload.checklistItems),
        position: payload.position,
      });
    case "meeting_task":
      return stripUndefined<Extract<AddItemDraft, { type: "meeting_task" }>>({
        type: "meeting_task",
        taskName: payload.taskName,
        taskDescription: payload.taskDescription ?? undefined,
        taskDueDate: payload.taskDueDate ?? undefined,
        meetingRecipientType: payload.meetingRecipientType,
        meetingRecipientUserId: payload.meetingRecipientUserId ?? undefined,
        meetingRecipientName: payload.meetingRecipientName ?? undefined,
        meetingInstructions: payload.meetingInstructions ?? undefined,
        meetingSchedulingLink: payload.meetingSchedulingLink ?? undefined,
        position: payload.position,
      });
    case "custom_task":
      return stripUndefined<Extract<AddItemDraft, { type: "custom_task" }>>({
        type: "custom_task",
        customTaskId: payload.customTaskId,
        taskDueDate: payload.taskDueDate ?? undefined,
        position: payload.position,
      });
    case "event_task":
      return stripUndefined<Extract<AddItemDraft, { type: "event_task" }>>({
        type: "event_task",
        taskName: payload.taskName,
        taskDescription: payload.taskDescription ?? undefined,
        taskDueDate: payload.taskDueDate ?? undefined,
        eventId: payload.eventId,
        eventName: payload.eventName ?? undefined,
        registrationRequired: payload.registrationRequired,
        attendanceRequired: payload.attendanceRequired,
        position: payload.position,
      });
    case "video": {
      if (payload.videoUrl === null) {
        return queuedDraft ?? null;
      }
      return stripUndefined<Extract<AddItemDraft, { type: "video" }>>({
        type: "video",
        taskName: payload.taskName ?? "",
        taskDescription: payload.taskDescription ?? undefined,
        taskDueDate: payload.taskDueDate ?? undefined,
        videoUrl: payload.videoUrl,
        videoProvider: payload.videoProvider ?? undefined,
        videoDurationSeconds: payload.videoDurationSeconds ?? undefined,
        watchedThresholdPercent: payload.watchedThresholdPercent ?? undefined,
        position: payload.position,
      });
    }
    case "image": {
      if (payload.imageUrl === null) {
        return queuedDraft ?? null;
      }
      return stripUndefined<Extract<AddItemDraft, { type: "image" }>>({
        type: "image",
        taskName: payload.taskName ?? "",
        taskDescription: payload.taskDescription ?? undefined,
        taskDueDate: payload.taskDueDate ?? undefined,
        imageUrl: payload.imageUrl,
        imageAlt: payload.imageAlt ?? undefined,
        position: payload.position,
      });
    }
    case "document_upload":
      return stripUndefined<Extract<AddItemDraft, { type: "document_upload" }>>(
        {
          type: "document_upload",
          taskName: payload.taskName ?? "",
          taskDescription: payload.taskDescription ?? undefined,
          taskDueDate: payload.taskDueDate ?? undefined,
          allowedMimeTypes: payload.allowedMimeTypes ?? undefined,
          maxFileSizeMb: payload.maxFileSizeMb ?? undefined,
          requiresReview: payload.requiresReview ?? undefined,
          position: payload.position,
        },
      );
    case "quiz": {
      // A quiz item always references a quiz; without one it can't be re-added.
      if (payload.quizId === null) {
        return queuedDraft ?? null;
      }
      return stripUndefined<Extract<AddItemDraft, { type: "quiz" }>>({
        type: "quiz",
        quizId: payload.quizId,
        quizTitle: payload.quizTitle ?? undefined,
        passingScore: payload.passingScore ?? undefined,
        position: payload.position,
      });
    }
    case "read_article_task": {
      if (payload.resourceId === null || payload.resourceUuid === null) {
        return queuedDraft ?? null;
      }
      return stripUndefined<
        Extract<AddItemDraft, { type: "read_article_task" }>
      >({
        type: "read_article_task",
        taskName: payload.taskName ?? payload.articleTitle ?? "Article",
        taskDescription: payload.taskDescription ?? undefined,
        resourceId: payload.resourceId,
        resourceUuid: payload.resourceUuid,
        articleTitle: payload.articleTitle ?? payload.taskName ?? "Article",
        articleUrl: payload.articleUrl ?? undefined,
        resourceType: payload.resourceType ?? undefined,
        additionalInstructions: payload.additionalInstructions ?? undefined,
        position: payload.position,
      });
    }
    case "coach_review":
      return stripUndefined<Extract<AddItemDraft, { type: "coach_review" }>>({
        type: "coach_review",
        taskName: payload.taskName ?? "Coach review",
        taskDescription: payload.taskDescription ?? undefined,
        reviewerType: payload.reviewerType,
        reviewerUserId: payload.reviewerUserId ?? undefined,
        position: payload.position,
      });
    case "text_block":
      return stripUndefined<Extract<AddItemDraft, { type: "text_block" }>>({
        type: "text_block",
        taskName: payload.taskName ?? "Text",
        taskDescription: payload.taskDescription ?? undefined,
        textContent: payload.textContent,
        position: payload.position,
      });
    case "ai_step":
      return stripUndefined<Extract<AddItemDraft, { type: "ai_step" }>>({
        type: "ai_step",
        taskName: payload.taskName ?? "AI step",
        taskDescription: payload.taskDescription ?? undefined,
        prompt: payload.prompt,
        mode: payload.mode,
        inputItemIds: payload.inputItemIds ?? undefined,
        defaultBranch: payload.defaultBranch ?? undefined,
        position: payload.position,
      });
    case "memory":
      return stripUndefined<Extract<AddItemDraft, { type: "memory" }>>({
        type: "memory",
        taskName: payload.taskName ?? "Memory",
        taskDescription: payload.taskDescription ?? undefined,
        memoryKey: payload.memoryKey,
        valueType: payload.valueType,
        options: payload.options.length > 0 ? payload.options : undefined,
        required: payload.required,
        scope: payload.scope,
        position: payload.position,
      });
    default:
      // profile_task: no add contract exists for it.
      return queuedDraft ?? null;
  }
};

/**
 * Inverse of a structural command, captured at dispatch time (BEFORE its
 * optimistic effect applies). Returns:
 * - a BuilderCommand → record {undo, redo} in history;
 * - "clear"          → the command is un-invertible (user deleteStep, a move
 *                      that empties+deletes its source step, conditional
 *                      edges) — history is wiped instead;
 * - null             → not history-worthy (canvas-only step patch, targets
 *                      that cannot be reconstructed).
 */
/** Builds the restoreStep inverse of deleting `stepClientId`, or null. */
const snapshotDeletedStep = (
  state: PathBuilderGraphState,
  stepClientId: string,
): BuilderCommand | null => {
  const step = state.steps[stepClientId];
  if (step === undefined) {
    return null;
  }
  const items: DeletedStepSnapshot["items"] = [];
  for (const itemClientId of step.itemIds) {
    const item = state.items[itemClientId];
    if (item === undefined) {
      return null;
    }
    const queuedAdd = state.queue.ops.find(
      (op) => op.kind === "addItem" && op.itemClientId === itemClientId,
    );
    const draft = reconstructAddDraft(
      item,
      queuedAdd !== undefined && queuedAdd.kind === "addItem"
        ? queuedAdd.draft
        : undefined,
    );
    if (draft === null) {
      return null;
    }
    items.push({
      draft,
      itemClientId,
      pendingTitle: derivePendingTitle(draft) ?? item.pendingTitle,
    });
  }
  const all = Object.values(state.connections);
  const incident = all.filter(
    (connection) =>
      connection.fromStepId === stepClientId ||
      connection.toStepId === stepClientId,
  );
  if (incident.some((connection) => connection.type === "conditional")) {
    return null;
  }
  return {
    kind: "restoreStep",
    snapshot: {
      alwaysBefore: all
        .filter((connection) => connection.type === "always")
        .map((connection) => `${connection.fromStepId}>${connection.toStepId}`),
      connections: incident.map((connection) => {
        const entry: DeletedStepSnapshot["connections"][number] = {
          connectionClientId: connection.clientId,
          fromStepClientId: connection.fromStepId,
          toStepClientId: connection.toStepId,
          type: connection.type as GraphAuthorableConnectionType,
        };
        if (connection.roleTag !== undefined) {
          entry.roleTag = connection.roleTag;
        }
        if (connection.tradeTag !== undefined) {
          entry.tradeTag = connection.tradeTag;
        }
        return entry;
      }),
      items,
      rechainSources: incident
        .filter((connection) => connection.toStepId === stepClientId)
        .map((connection) => connection.fromStepId),
      rechainTargets: incident
        .filter((connection) => connection.fromStepId === stepClientId)
        .map((connection) => connection.toStepId),
      step: {
        canvasX: step.canvasX,
        canvasY: step.canvasY,
        clientId: step.clientId,
        label: step.label,
        partLabel: step.partLabel ?? null,
        requireAll: step.requireAll,
        unlockAfterDays: step.unlockAfterDays ?? null,
        unlockMemoryKey: step.unlockMemoryKey ?? null,
        unlockMemoryRule: step.unlockMemoryRule ?? null,
      },
    },
  };
};

const invertCommand = (
  state: PathBuilderGraphState,
  command: BuilderCommand,
): BuilderCommand | "clear" | null => {
  switch (command.kind) {
    case "createStep":
    case "createStepWithItem":
      return { kind: "deleteStep", stepClientId: command.stepClientId };
    case "updateStep": {
      const step = state.steps[command.stepClientId];
      if (step === undefined) {
        return null;
      }
      const structural =
        command.changes.label !== undefined ||
        command.changes.partLabel !== undefined ||
        command.changes.requireAll !== undefined ||
        command.changes.unlockAfterDays !== undefined ||
        command.changes.unlockMemoryKey !== undefined ||
        command.changes.unlockMemoryRule !== undefined;
      if (!structural) {
        // Position-only patches are excluded from history (like drags).
        return null;
      }
      const prev: StepChanges = {};
      if (command.changes.label !== undefined) {
        prev.label = step.label;
      }
      if (command.changes.partLabel !== undefined) {
        prev.partLabel = step.partLabel;
      }
      if (command.changes.requireAll !== undefined) {
        prev.requireAll = step.requireAll;
      }
      if (command.changes.unlockAfterDays !== undefined) {
        prev.unlockAfterDays = step.unlockAfterDays;
      }
      if (command.changes.unlockMemoryKey !== undefined) {
        prev.unlockMemoryKey = step.unlockMemoryKey ?? null;
      }
      if (command.changes.unlockMemoryRule !== undefined) {
        prev.unlockMemoryRule = step.unlockMemoryRule ?? null;
      }
      if (command.changes.canvasX !== undefined) {
        prev.canvasX = step.canvasX;
      }
      if (command.changes.canvasY !== undefined) {
        prev.canvasY = step.canvasY;
      }
      return {
        kind: "updateStep",
        stepClientId: command.stepClientId,
        changes: prev,
      };
    }
    case "deleteStep":
      // Undoable via a restoreStep snapshot; items that can't be rebuilt
      // client-side (or score-branch edges) still wipe history.
      return snapshotDeletedStep(state, command.stepClientId) ?? "clear";
    case "batch":
    case "restoreStep":
      // Produced by history itself; never dispatched as a user command.
      return null;
    case "addItem":
      return { kind: "deleteItem", itemClientId: command.itemClientId };
    case "updateItem": {
      const item = state.items[command.itemClientId];
      if (item === undefined || item.payload === null) {
        return null;
      }
      return {
        kind: "updateItem",
        itemClientId: command.itemClientId,
        changes: captureItemPrev(item.payload, command.changes),
      };
    }
    case "deleteItem": {
      const item = state.items[command.itemClientId];
      if (item === undefined) {
        return null;
      }
      const queuedAdd = state.queue.ops.find(
        (op) =>
          op.kind === "addItem" && op.itemClientId === command.itemClientId,
      );
      const draft = reconstructAddDraft(
        item,
        queuedAdd !== undefined && queuedAdd.kind === "addItem"
          ? queuedAdd.draft
          : undefined,
      );
      if (draft === null) {
        return null;
      }
      return {
        kind: "addItem",
        stepClientId: item.stepClientId,
        itemClientId: command.itemClientId,
        draft,
        pendingTitle: derivePendingTitle(draft) ?? item.pendingTitle,
      };
    }
    case "moveItem": {
      const item = state.items[command.itemClientId];
      if (item === undefined) {
        return null;
      }
      const fromStep = state.steps[item.stepClientId];
      if (fromStep === undefined) {
        return null;
      }
      const sourceEmptied = fromStep.itemIds.length === 1;
      if (sourceEmptied && command.keepEmptyStep !== true) {
        // The server deletes the emptied source step and re-chains edges
        // we cannot reconstruct.
        return "clear";
      }
      return {
        kind: "moveItem",
        itemClientId: command.itemClientId,
        toStepClientId: item.stepClientId,
        toIndex: fromStep.itemIds.indexOf(command.itemClientId),
        position: item.position,
        // Restore EXACT prior structure: the target of the inverse existed
        // before the forward move, so never let the server delete it.
        keepEmptyStep: true,
      };
    }
    case "reorderWithinStep": {
      const step = state.steps[command.stepClientId];
      if (step === undefined) {
        return null;
      }
      return {
        kind: "reorderWithinStep",
        stepClientId: command.stepClientId,
        orderedItemClientIds: [...step.itemIds],
      };
    }
    case "addConnection":
      return {
        kind: "deleteConnection",
        connectionClientId: command.connectionClientId,
      };
    case "updateConnection": {
      const connection = state.connections[command.connectionClientId];
      if (connection === undefined) {
        return null;
      }
      if (connection.type === "conditional") {
        // Variable rules edit in place; anything else on a conditional edge
        // is not undoable here.
        if (
          command.changes.conditionBranch !== undefined &&
          connection.conditionBranch !== undefined &&
          Object.keys(command.changes).length === 1
        ) {
          return {
            kind: "updateConnection",
            connectionClientId: command.connectionClientId,
            changes: { conditionBranch: connection.conditionBranch },
          };
        }
        return "clear";
      }
      const prev: UpdateGraphConnectionFields = {};
      if (command.changes.type !== undefined) {
        prev.type = connection.type as GraphAuthorableConnectionType;
        // A retype may clear tags server-side; capture both for restore.
        prev.roleTag = connection.roleTag ?? null;
        prev.tradeTag = connection.tradeTag ?? null;
      }
      if (command.changes.roleTag !== undefined) {
        prev.roleTag = connection.roleTag ?? null;
      }
      if (command.changes.tradeTag !== undefined) {
        prev.tradeTag = connection.tradeTag ?? null;
      }
      return {
        kind: "updateConnection",
        connectionClientId: command.connectionClientId,
        changes: prev,
      };
    }
    case "deleteConnection": {
      const connection = state.connections[command.connectionClientId];
      if (connection === undefined) {
        return null;
      }
      if (connection.type === "conditional") {
        // A value-mode edge (Memory rule / decision option) can be re-added
        // with its source + branch; quiz edges (threshold) cannot.
        if (
          connection.scoreThreshold === undefined &&
          connection.conditionSourceItemId !== undefined &&
          connection.conditionBranch !== undefined
        ) {
          return {
            kind: "addConnection",
            connectionClientId: command.connectionClientId,
            fromStepClientId: connection.fromStepId,
            toStepClientId: connection.toStepId,
            type: "conditional",
            conditionSourceItemClientId: connection.conditionSourceItemId,
            conditionBranch: connection.conditionBranch,
          };
        }
        return "clear";
      }
      const inverse: Extract<BuilderCommand, { kind: "addConnection" }> = {
        kind: "addConnection",
        connectionClientId: command.connectionClientId,
        fromStepClientId: connection.fromStepId,
        toStepClientId: connection.toStepId,
        type: connection.type as GraphAuthorableConnectionType,
      };
      if (connection.roleTag !== undefined) {
        inverse.roleTag = connection.roleTag;
      }
      if (connection.tradeTag !== undefined) {
        inverse.tradeTag = connection.tradeTag;
      }
      return inverse;
    }
  }
};

/** Pre-flight check before replaying a history entry's command. */
const commandTargetsExist = (
  state: PathBuilderGraphState,
  command: BuilderCommand,
): boolean => {
  switch (command.kind) {
    case "createStep":
      return state.steps[command.stepClientId] === undefined;
    case "createStepWithItem":
      return (
        state.steps[command.stepClientId] === undefined &&
        state.items[command.itemClientId] === undefined
      );
    case "updateStep":
    case "deleteStep":
      return state.steps[command.stepClientId] !== undefined;
    case "addItem":
      return (
        state.steps[command.stepClientId] !== undefined &&
        state.items[command.itemClientId] === undefined
      );
    case "updateItem":
    case "deleteItem":
      return state.items[command.itemClientId] !== undefined;
    case "moveItem": {
      const item = state.items[command.itemClientId];
      return (
        item !== undefined &&
        state.steps[command.toStepClientId] !== undefined &&
        item.stepClientId !== command.toStepClientId
      );
    }
    case "reorderWithinStep": {
      const step = state.steps[command.stepClientId];
      if (step === undefined) {
        return false;
      }
      const current = [...step.itemIds].sort();
      const ordered = [...command.orderedItemClientIds].sort();
      return (
        current.length === ordered.length &&
        current.every((itemId, index) => itemId === ordered[index])
      );
    }
    case "addConnection":
      return (
        state.connections[command.connectionClientId] === undefined &&
        state.steps[command.fromStepClientId] !== undefined &&
        state.steps[command.toStepClientId] !== undefined
      );
    case "updateConnection":
    case "deleteConnection":
      return state.connections[command.connectionClientId] !== undefined;
    case "batch":
      // Sub-commands are re-checked one by one as the batch replays.
      return (
        command.commands.length > 0 &&
        commandTargetsExist(state, command.commands[0])
      );
    case "restoreStep":
      return state.steps[command.snapshot.step.clientId] === undefined;
  }
};

/**
 * Applies a command: optimistic state mutation + queue op(s), then pumps.
 * Never records history itself — callers decide (dispatchStructuralCommand
 * records; undo/redo replay without re-recording).
 */
const applyCommand =
  (command: BuilderCommand): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    if (command.kind === "batch") {
      command.commands.forEach((sub) => {
        if (commandTargetsExist(getState().pathBuilderGraph, sub)) {
          dispatch(applyCommand(sub));
        }
      });
      return;
    }
    if (command.kind === "restoreStep") {
      dispatch(applyRestoreStep(command.snapshot));
      return;
    }
    switch (command.kind) {
      case "createStep": {
        dispatch(
          actions.stepAddedLocally({
            clientId: command.stepClientId,
            label: command.label,
            requireAll: command.requireAll,
            canvasX: command.canvasX,
            canvasY: command.canvasY,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "createStep",
            stepClientId: command.stepClientId,
            label: command.label,
            requireAll: command.requireAll,
            canvasX: command.canvasX,
            canvasY: command.canvasY,
          }),
        );
        break;
      }
      case "createStepWithItem": {
        dispatch(
          actions.stepAddedLocally({
            clientId: command.stepClientId,
            label: command.label,
            requireAll: command.requireAll,
            canvasX: command.canvasX,
            canvasY: command.canvasY,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "createStep",
            stepClientId: command.stepClientId,
            label: command.label,
            requireAll: command.requireAll,
            canvasX: command.canvasX,
            canvasY: command.canvasY,
          }),
        );
        dispatch(
          actions.itemAddedLocally({
            clientId: command.itemClientId,
            stepClientId: command.stepClientId,
            type: command.draft.type,
            position: command.draft.position,
            pendingTitle: command.pendingTitle,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "addItem",
            stepClientId: command.stepClientId,
            itemClientId: command.itemClientId,
            draft: command.draft,
          }),
        );
        break;
      }
      case "updateStep": {
        dispatch(
          actions.stepPatchedLocally({
            clientId: command.stepClientId,
            changes: command.changes,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "updateStep",
            stepClientId: command.stepClientId,
            changes: command.changes,
          }),
        );
        break;
      }
      case "deleteStep": {
        // Late-binding guard (M4 review minor #4): when the LIVE step's
        // serverId is still null (its create op is in flight, e.g. a fast
        // redo→undo), do NOT bake in the pairings entry — that can be the
        // STALE id of a previously deleted incarnation under the same
        // clientId. Leaving it null makes executeOp resolve through the
        // fresh pairing written by stepConfirmed.
        const liveStep =
          getState().pathBuilderGraph.steps[command.stepClientId];
        const stepServerId =
          liveStep !== undefined && liveStep.serverId === null
            ? null
            : resolveStepServerId(
                getState().pathBuilderGraph,
                command.stepClientId,
              );
        dispatch(
          actions.stepRemovedLocally({ clientId: command.stepClientId }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "deleteStep",
            stepClientId: command.stepClientId,
            stepServerId,
          }),
        );
        break;
      }
      case "addItem": {
        dispatch(
          actions.itemAddedLocally({
            clientId: command.itemClientId,
            stepClientId: command.stepClientId,
            type: command.draft.type,
            position: command.draft.position,
            pendingTitle: command.pendingTitle,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "addItem",
            stepClientId: command.stepClientId,
            itemClientId: command.itemClientId,
            draft: command.draft,
          }),
        );
        break;
      }
      case "updateItem": {
        dispatch(
          actions.itemPatchedLocally({
            clientId: command.itemClientId,
            changes: command.changes,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "updateItem",
            itemClientId: command.itemClientId,
            changes: command.changes,
          }),
        );
        break;
      }
      case "deleteItem": {
        dispatch(
          actions.itemRemovedLocally({ clientId: command.itemClientId }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "deleteItem",
            itemClientId: command.itemClientId,
          }),
        );
        break;
      }
      case "moveItem": {
        const fromStepClientId =
          getState().pathBuilderGraph.items[command.itemClientId]?.stepClientId;
        if (fromStepClientId === undefined) {
          throw new Error(
            `pathBuilderGraph: unknown item ${command.itemClientId}`,
          );
        }
        dispatch(
          actions.itemMovedLocally({
            clientId: command.itemClientId,
            toStepClientId: command.toStepClientId,
            toIndex: command.toIndex,
          }),
        );
        const op: Extract<QueueOp, { kind: "moveItem" }> = {
          opId: nanoid(),
          kind: "moveItem",
          itemClientId: command.itemClientId,
          fromStepClientId,
          toStepClientId: command.toStepClientId,
        };
        if (command.position !== undefined) {
          op.position = command.position;
        }
        if (command.keepEmptyStep !== undefined) {
          op.keepEmptyStep = command.keepEmptyStep;
        }
        dispatch(actions.opEnqueued(op));
        break;
      }
      case "reorderWithinStep": {
        dispatch(
          actions.stepItemsReorderedLocally({
            stepClientId: command.stepClientId,
            orderedItemClientIds: command.orderedItemClientIds,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "reorderWithinStep",
            stepClientId: command.stepClientId,
            orderedItemClientIds: command.orderedItemClientIds,
          }),
        );
        break;
      }
      case "addConnection": {
        dispatch(
          actions.connectionAddedLocally({
            clientId: command.connectionClientId,
            fromStepClientId: command.fromStepClientId,
            toStepClientId: command.toStepClientId,
            type: command.type,
            roleTag: command.roleTag,
            tradeTag: command.tradeTag,
            conditionSourceItemClientId: command.conditionSourceItemClientId,
            conditionBranch: command.conditionBranch,
          }),
        );
        const op: Extract<QueueOp, { kind: "addConnection" }> = {
          opId: nanoid(),
          kind: "addConnection",
          connectionClientId: command.connectionClientId,
          fromStepClientId: command.fromStepClientId,
          toStepClientId: command.toStepClientId,
          type: command.type,
        };
        if (command.roleTag !== undefined) {
          op.roleTag = command.roleTag;
        }
        if (command.tradeTag !== undefined) {
          op.tradeTag = command.tradeTag;
        }
        if (command.conditionSourceItemClientId !== undefined) {
          op.conditionSourceItemClientId = command.conditionSourceItemClientId;
        }
        if (command.conditionBranch !== undefined) {
          op.conditionBranch = command.conditionBranch;
        }
        dispatch(actions.opEnqueued(op));
        break;
      }
      case "updateConnection": {
        dispatch(
          actions.connectionPatchedLocally({
            clientId: command.connectionClientId,
            changes: command.changes,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "updateConnection",
            connectionClientId: command.connectionClientId,
            changes: command.changes,
          }),
        );
        break;
      }
      case "deleteConnection": {
        dispatch(
          actions.connectionRemovedLocally({
            clientId: command.connectionClientId,
          }),
        );
        dispatch(
          actions.opEnqueued({
            opId: nanoid(),
            kind: "deleteConnection",
            connectionClientId: command.connectionClientId,
          }),
        );
        break;
      }
    }
    void dispatch(pumpQueue());
  };

/**
 * Undo of a step delete: drop the 'always' edges the server re-chained
 * around the step, then recreate the step (same clientId), its items and
 * its connections — all through the normal queue.
 */
const applyRestoreStep =
  (snapshot: DeletedStepSnapshot): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    const before = new Set(snapshot.alwaysBefore);
    const sources = new Set(snapshot.rechainSources);
    const targets = new Set(snapshot.rechainTargets);
    Object.values(getState().pathBuilderGraph.connections).forEach(
      (connection) => {
        if (
          connection.type === "always" &&
          sources.has(connection.fromStepId) &&
          targets.has(connection.toStepId) &&
          !before.has(`${connection.fromStepId}>${connection.toStepId}`)
        ) {
          dispatch(
            applyCommand({
              kind: "deleteConnection",
              connectionClientId: connection.clientId,
            }),
          );
        }
      },
    );
    const { step } = snapshot;
    dispatch(
      applyCommand({
        kind: "createStep",
        stepClientId: step.clientId,
        label: step.label,
        requireAll: step.requireAll,
        canvasX: step.canvasX,
        canvasY: step.canvasY,
      }),
    );
    if (step.unlockAfterDays !== null && step.unlockAfterDays > 0) {
      dispatch(
        applyCommand({
          kind: "updateStep",
          stepClientId: step.clientId,
          changes: { unlockAfterDays: step.unlockAfterDays },
        }),
      );
    }
    if (step.unlockMemoryKey !== null && step.unlockMemoryRule !== null) {
      dispatch(
        applyCommand({
          kind: "updateStep",
          stepClientId: step.clientId,
          changes: {
            unlockMemoryKey: step.unlockMemoryKey,
            unlockMemoryRule: step.unlockMemoryRule,
          },
        }),
      );
    }
    if (step.partLabel) {
      dispatch(
        applyCommand({
          kind: "updateStep",
          stepClientId: step.clientId,
          changes: { partLabel: step.partLabel },
        }),
      );
    }
    snapshot.items.forEach((item) => {
      dispatch(
        applyCommand({
          kind: "addItem",
          stepClientId: step.clientId,
          itemClientId: item.itemClientId,
          draft: item.draft,
          pendingTitle: item.pendingTitle,
        }),
      );
    });
    snapshot.connections.forEach((connection) => {
      const current = getState().pathBuilderGraph;
      if (
        current.steps[connection.fromStepClientId] !== undefined &&
        current.steps[connection.toStepClientId] !== undefined &&
        current.connections[connection.connectionClientId] === undefined
      ) {
        dispatch(applyCommand({ kind: "addConnection", ...connection }));
      }
    });
  };

/** True when replaying `command` needs the server's post-delete refetch. */
const commandRestoresStep = (command: BuilderCommand): boolean =>
  command.kind === "restoreStep" ||
  (command.kind === "batch" && command.commands.some(commandRestoresStep));

/** Open recordAsOneEdit group, if any (module scope, like the debounce). */
let historyGroup: { entries: HistoryEntry[]; clear: boolean } | null = null;

/**
 * Runs `edit` so every structural command it dispatches lands in history as
 * ONE entry (undo replays the inverses in reverse order; redo replays the
 * originals in order). Nested calls join the outer group.
 */
export const recordAsOneEdit =
  <R>(edit: () => R): PathBuilderGraphThunk<R> =>
  (dispatch) => {
    if (historyGroup !== null) {
      return edit();
    }
    const group: { entries: HistoryEntry[]; clear: boolean } = {
      clear: false,
      entries: [],
    };
    historyGroup = group;
    try {
      return edit();
    } finally {
      historyGroup = null;
      if (group.clear) {
        dispatch(actions.historyCleared());
      } else if (group.entries.length === 1) {
        dispatch(actions.historyRecorded({ entry: group.entries[0] }));
      } else if (group.entries.length > 1) {
        dispatch(
          actions.historyRecorded({
            entry: {
              undo: {
                kind: "batch",
                commands: group.entries.map((entry) => entry.undo).reverse(),
              },
              redo: {
                kind: "batch",
                commands: group.entries.map((entry) => entry.redo),
              },
            },
          }),
        );
      }
    }
  };

/**
 * Records the command's inverse (captured NOW — patch-with-prev semantics)
 * and applies it. Un-invertible commands wipe history instead of recording.
 * Inside recordAsOneEdit the entry joins the open group instead.
 */
const dispatchStructuralCommand =
  (command: BuilderCommand): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    const inverse = invertCommand(getState().pathBuilderGraph, command);
    if (historyGroup !== null) {
      if (inverse === "clear") {
        historyGroup.clear = true;
      } else if (inverse !== null) {
        historyGroup.entries.push({ undo: inverse, redo: command });
      }
    } else if (inverse === "clear") {
      dispatch(actions.historyCleared());
    } else if (inverse !== null) {
      dispatch(
        actions.historyRecorded({ entry: { undo: inverse, redo: command } }),
      );
    }
    dispatch(applyCommand(command));
  };

// --- Position debounce buffer (module scope; one builder mounts at a time) -------

/** Debounce window for coalescing node-drag position changes. */
export const POSITION_DEBOUNCE_MS = 500;

let positionDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPositionChanges = new Map<
  string,
  { canvasX: number | null; canvasY: number | null }
>();

const clearPositionDebounce = (): void => {
  if (positionDebounceTimer !== null) {
    clearTimeout(positionDebounceTimer);
    positionDebounceTimer = null;
  }
};

/** Test-only: clears the module-scoped debounce timer and buffer. */
export const __resetPathBuilderGraphModuleStateForTests = (): void => {
  historyGroup = null;
  clearPositionDebounce();
  pendingPositionChanges = new Map();
};

// --- Public thunks -----------------------------------------------------------------

/** Loads GET /graph into the slice (initial load and explicit reloads). */
export const loadPathGraph =
  (pathId: number): PathBuilderGraphThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(actions.graphLoadStarted());
    try {
      const response = await getPathGraph(pathId);
      dispatch(actions.graphLoaded(response.data));
    } catch (error) {
      dispatch(
        actions.graphLoadFailed({ message: extractErrorMessage(error) }),
      );
    }
  };

/** Clears all builder state (unmount / path switch). */
export const resetBuilder = (): PathBuilderGraphThunk => (dispatch) => {
  clearPositionDebounce();
  pendingPositionChanges = new Map();
  dispatch(actions.builderStateReset());
};

const requireKnownStep = (
  state: PathBuilderGraphRootState,
  stepClientId: string,
): BuilderStep => {
  const step = state.pathBuilderGraph.steps[stepClientId];
  if (step === undefined) {
    throw new Error(`pathBuilderGraph: unknown step ${stepClientId}`);
  }
  return step;
};

const requireKnownItem = (
  state: PathBuilderGraphRootState,
  itemClientId: string,
): BuilderItem => {
  const item = state.pathBuilderGraph.items[itemClientId];
  if (item === undefined) {
    throw new Error(`pathBuilderGraph: unknown item ${itemClientId}`);
  }
  return item;
};

const requireKnownConnection = (
  state: PathBuilderGraphRootState,
  connectionClientId: string,
): BuilderConnection => {
  const connection = state.pathBuilderGraph.connections[connectionClientId];
  if (connection === undefined) {
    throw new Error(
      `pathBuilderGraph: unknown connection ${connectionClientId}`,
    );
  }
  return connection;
};

/**
 * Optimistically adds a step and queues POST /steps.
 * Returns the new step's clientId (usable immediately as a dependency for
 * enqueueAddItem / enqueueAddConnection — the queue resolves the serverId
 * once known). Undoable (inverse: deleteStep).
 */
export const enqueueCreateStep =
  (input: {
    label?: string | null;
    requireAll?: boolean;
    canvasX: number | null;
    canvasY: number | null;
  }): PathBuilderGraphThunk<string> =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    const stepClientId = nanoid();
    dispatch(
      dispatchStructuralCommand({
        kind: "createStep",
        stepClientId,
        label: input.label ?? null,
        requireAll: input.requireAll ?? true,
        canvasX: input.canvasX,
        canvasY: input.canvasY,
      }),
    );
    return stepClientId;
  };

/**
 * Optimistically patches a step and queues PATCH /steps/{stepId}.
 * label/requireAll changes are undoable (patch-with-prev); canvas-only
 * patches are positional and excluded from history.
 */
export const enqueueUpdateStep =
  (input: {
    stepClientId: string;
    changes: StepChanges;
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownStep(getState(), input.stepClientId);
    dispatch(
      dispatchStructuralCommand({
        kind: "updateStep",
        stepClientId: input.stepClientId,
        changes: input.changes,
      }),
    );
  };

/**
 * Optimistically removes a step (with its items and incident connections)
 * and queues DELETE /steps/{stepId}. After the delete succeeds, the slice
 * reconciles via GET /graph because the backend re-chains
 * predecessor→successor 'always' edges. A 422 (contained item has completion
 * records — RESTRICT) flows through the standard failure path, which
 * restores the step from the refetch. NOT undoable — clears history.
 */
export const enqueueDeleteStep =
  (input: { stepClientId: string }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    const step = requireKnownStep(getState(), input.stepClientId);
    if (step.serverId === null) {
      throw new Error(
        "pathBuilderGraph: cannot delete a step that is still being created",
      );
    }
    dispatch(
      dispatchStructuralCommand({
        kind: "deleteStep",
        stepClientId: input.stepClientId,
      }),
    );
  };

/**
 * Optimistically adds an item of ANY concrete type to a step and queues
 * POST /steps/{stepId}/items. `item` is the per-type request payload minus
 * expectedUpdatedAt; position (path-wide) defaults to end-of-step
 * server-side. Returns the new item's clientId. Undoable (inverse:
 * deleteItem with late-bound serverId).
 */
export const enqueueAddItem =
  (input: {
    stepClientId: string;
    item: AddItemDraft;
    /** Display title for the chip while the create is in flight. */
    pendingTitle?: string;
  }): PathBuilderGraphThunk<string> =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownStep(getState(), input.stepClientId);
    const itemClientId = nanoid();
    const draft = stripUndefined(input.item);
    dispatch(
      dispatchStructuralCommand({
        kind: "addItem",
        stepClientId: input.stepClientId,
        itemClientId,
        draft,
        pendingTitle: input.pendingTitle ?? derivePendingTitle(draft),
      }),
    );
    return itemClientId;
  };

/**
 * Convenience wrapper over enqueueAddItem for course drops (the M3 surface
 * the canvas/palette already use). Returns the new item's clientId.
 */
export const enqueueAddCourseItem =
  (input: {
    stepClientId: string;
    courseId: number;
    position?: number;
    /** Optional display title for the chip while the create is in flight. */
    courseName?: string;
  }): PathBuilderGraphThunk<string> =>
  (dispatch) =>
    dispatch(
      enqueueAddItem({
        stepClientId: input.stepClientId,
        item: {
          type: "course",
          courseId: input.courseId,
          position: input.position,
        },
        pendingTitle: input.courseName,
      }),
    );

/**
 * Compound pane-drop: optimistically creates a step AND its first item,
 * queueing POST /steps then POST /steps/{stepId}/items (the add is
 * dependency-blocked behind the create by FIFO order). ONE history entry —
 * undo deletes the whole step. Returns both clientIds.
 */
export const enqueueCreateStepWithItem =
  (input: {
    label?: string | null;
    requireAll?: boolean;
    canvasX: number | null;
    canvasY: number | null;
    item: AddItemDraft;
    pendingTitle?: string;
  }): PathBuilderGraphThunk<{ stepClientId: string; itemClientId: string }> =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    const stepClientId = nanoid();
    const itemClientId = nanoid();
    const draft = stripUndefined(input.item);
    dispatch(
      dispatchStructuralCommand({
        kind: "createStepWithItem",
        stepClientId,
        itemClientId,
        label: input.label ?? null,
        requireAll: input.requireAll ?? true,
        canvasX: input.canvasX,
        canvasY: input.canvasY,
        draft,
        pendingTitle: input.pendingTitle ?? derivePendingTitle(draft),
      }),
    );
    return { stepClientId, itemClientId };
  };

/**
 * Optimistically patches an item (partial, per-type fields) and queues the
 * generic PATCH /items/{itemId}. Undoable (patch-with-prev captured at
 * dispatch time).
 */
export const enqueueUpdateItem =
  (input: {
    itemClientId: string;
    changes: UpdateGraphItemFields;
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownItem(getState(), input.itemClientId);
    dispatch(
      dispatchStructuralCommand({
        kind: "updateItem",
        itemClientId: input.itemClientId,
        changes: stripUndefined(input.changes),
      }),
    );
  };

/**
 * Optimistically removes an item and queues DELETE /items/{itemId} (the
 * serverId is late-bound — resolved from the pairing map at execution).
 * Undoable when the add-draft can be reconstructed from the server payload.
 */
export const enqueueDeleteItem =
  (input: { itemClientId: string }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownItem(getState(), input.itemClientId);
    dispatch(
      dispatchStructuralCommand({
        kind: "deleteItem",
        itemClientId: input.itemClientId,
      }),
    );
  };

/**
 * Optimistically moves an item between steps and queues
 * POST /items/{itemId}/move. `position` is PATH-WIDE (server convention);
 * `toIndex` is the local insert index for optimistic ordering — both
 * default to end-of-step. Moving the LAST item out of a step lets the
 * server delete the emptied step (reconciled via refetch) unless
 * keepEmptyStep is true; that destructive variant clears history instead
 * of recording an inverse.
 */
export const enqueueMoveItem =
  (input: {
    itemClientId: string;
    toStepClientId: string;
    toIndex?: number;
    position?: number;
    keepEmptyStep?: boolean;
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    const item = requireKnownItem(getState(), input.itemClientId);
    requireKnownStep(getState(), input.toStepClientId);
    if (item.stepClientId === input.toStepClientId) {
      throw new Error(
        "pathBuilderGraph: use enqueueReorderStepItems for same-step moves",
      );
    }
    const command: Extract<BuilderCommand, { kind: "moveItem" }> = {
      kind: "moveItem",
      itemClientId: input.itemClientId,
      toStepClientId: input.toStepClientId,
    };
    if (input.toIndex !== undefined) {
      command.toIndex = input.toIndex;
    }
    if (input.position !== undefined) {
      command.position = input.position;
    }
    if (input.keepEmptyStep !== undefined) {
      command.keepEmptyStep = input.keepEmptyStep;
    }
    dispatch(dispatchStructuralCommand(command));
  };

/**
 * Optimistically reorders a step's items and queues
 * PUT /steps/{stepId}/items/reorder. orderedItemClientIds MUST be a
 * COMPLETE permutation of the step's current items (throws otherwise —
 * the server 422s partial maps). Undoable (inverse: previous order).
 */
export const enqueueReorderStepItems =
  (input: {
    stepClientId: string;
    orderedItemClientIds: string[];
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    const step = requireKnownStep(getState(), input.stepClientId);
    const current = [...step.itemIds].sort();
    const ordered = [...input.orderedItemClientIds].sort();
    const completePermutation =
      current.length === ordered.length &&
      current.every((itemId, index) => itemId === ordered[index]);
    if (!completePermutation) {
      throw new Error(
        "pathBuilderGraph: reorder requires a complete permutation of the step's items",
      );
    }
    dispatch(
      dispatchStructuralCommand({
        kind: "reorderWithinStep",
        stepClientId: input.stepClientId,
        orderedItemClientIds: [...input.orderedItemClientIds],
      }),
    );
  };

/**
 * Optimistically adds a connection and queues POST /connections. Endpoint
 * steps may still be un-persisted — the op dependency-blocks behind their
 * creates (FIFO). type defaults to 'always'; 'conditional' is not
 * authorable (arrives with quizzes). Server-side 422s (cycle, duplicate
 * predicate, >1 'always' outgoing) flow through the failure truth-restore.
 * Returns the new connection's clientId. Undoable (inverse:
 * deleteConnection).
 */
export const enqueueAddConnection =
  (input: {
    fromStepClientId: string;
    toStepClientId: string;
    type?: GraphAuthorableConnectionType | "conditional";
    roleTag?: GraphRoleTag;
    tradeTag?: GraphTradeTag;
    /** For type "conditional": the Memory/decision item (clientId) and its rule / option key. */
    conditionSourceItemClientId?: string;
    conditionBranch?: GraphConditionBranch;
  }): PathBuilderGraphThunk<string> =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownStep(getState(), input.fromStepClientId);
    requireKnownStep(getState(), input.toStepClientId);
    if (input.fromStepClientId === input.toStepClientId) {
      throw new Error("pathBuilderGraph: connections cannot self-loop");
    }
    if (
      input.type === "conditional" &&
      (input.conditionSourceItemClientId === undefined ||
        input.conditionBranch === undefined)
    ) {
      throw new Error(
        "pathBuilderGraph: a conditional connection needs a source item and a branch",
      );
    }
    const connectionClientId = nanoid();
    const command: Extract<BuilderCommand, { kind: "addConnection" }> = {
      kind: "addConnection",
      connectionClientId,
      fromStepClientId: input.fromStepClientId,
      toStepClientId: input.toStepClientId,
      type: input.type ?? "always",
    };
    if (input.roleTag !== undefined) {
      command.roleTag = input.roleTag;
    }
    if (input.tradeTag !== undefined) {
      command.tradeTag = input.tradeTag;
    }
    if (input.conditionSourceItemClientId !== undefined) {
      command.conditionSourceItemClientId = input.conditionSourceItemClientId;
    }
    if (input.conditionBranch !== undefined) {
      command.conditionBranch = input.conditionBranch;
    }
    dispatch(dispatchStructuralCommand(command));
    return connectionClientId;
  };

/**
 * Optimistically retypes/retags a connection and queues
 * PATCH /connections/{connectionId}. null clears a tag. Undoable
 * (patch-with-prev; a retype captures both tags).
 */
export const enqueueUpdateConnection =
  (input: {
    connectionClientId: string;
    changes: UpdateGraphConnectionFields;
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownConnection(getState(), input.connectionClientId);
    dispatch(
      dispatchStructuralCommand({
        kind: "updateConnection",
        connectionClientId: input.connectionClientId,
        changes: stripUndefined(input.changes),
      }),
    );
  };

/**
 * Optimistically removes a connection and queues
 * DELETE /connections/{connectionId}. Edges are pure structure —
 * orphan-reachability warnings are the canvas's job. Undoable (inverse:
 * addConnection with the captured predicate fields).
 */
export const enqueueDeleteConnection =
  (input: { connectionClientId: string }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    requireKnownConnection(getState(), input.connectionClientId);
    dispatch(
      dispatchStructuralCommand({
        kind: "deleteConnection",
        connectionClientId: input.connectionClientId,
      }),
    );
  };

/**
 * Undo the most recent structural edit by replaying its captured inverse
 * through the SAME mutation queue (pendingOps increments; the server stays
 * consistent because undo is just more ops). Returns true when an entry
 * was replayed. No-ops while failure recovery is in progress.
 */
export const undoGraphEdit =
  (): PathBuilderGraphThunk<boolean> => (dispatch, getState) => {
    const slice = getState().pathBuilderGraph;
    if (slice.queue.paused) {
      return false;
    }
    const entry = slice.history.past[slice.history.past.length - 1];
    if (entry === undefined) {
      return false;
    }
    if (
      commandRestoresStep(entry.undo) &&
      (slice.queue.running || slice.queue.ops.length > 0)
    ) {
      // The delete's re-chained edges aren't known until its save (and
      // refetch) completes — callers tell the user to retry in a moment.
      return false;
    }
    if (!commandTargetsExist(slice, entry.undo)) {
      // The graph diverged from what this entry assumed — drop history
      // rather than replay a broken command.
      dispatch(actions.historyCleared());
      return false;
    }
    dispatch(actions.historyUndone());
    dispatch(applyCommand(entry.undo));
    return true;
  };

/**
 * undoGraphEdit plus WHY nothing happened: "saving" when the top entry
 * restores a deleted step whose save is still in flight (callers toast
 * "try again in a moment").
 */
export const undoGraphEditExplained =
  (): PathBuilderGraphThunk<"undone" | "saving" | "nothing"> =>
  (dispatch, getState) => {
    if (dispatch(undoGraphEdit())) {
      return "undone";
    }
    const slice = getState().pathBuilderGraph;
    const top = slice.history.past[slice.history.past.length - 1];
    return top !== undefined &&
      commandRestoresStep(top.undo) &&
      (slice.queue.running || slice.queue.ops.length > 0)
      ? "saving"
      : "nothing";
  };

/**
 * Redo the most recently undone edit (replays the original forward command
 * under its ORIGINAL clientIds, keeping deeper history entries valid).
 * Returns true when an entry was replayed.
 */
export const redoGraphEdit =
  (): PathBuilderGraphThunk<boolean> => (dispatch, getState) => {
    const slice = getState().pathBuilderGraph;
    if (slice.queue.paused) {
      return false;
    }
    const entry = slice.history.future[slice.history.future.length - 1];
    if (entry === undefined) {
      return false;
    }
    if (!commandTargetsExist(slice, entry.redo)) {
      dispatch(actions.historyCleared());
      return false;
    }
    dispatch(actions.historyRedone());
    dispatch(applyCommand(entry.redo));
    return true;
  };

/**
 * Duplicates the loaded path (POST /duplicate — deep copy, always draft,
 * default name '<source> (copy)'). DIRECT thunk, NOT a queue op: the source
 * graph is only read, so it bypasses the mutation queue and concurrency
 * token entirely. Resolves to the NEW path's id (navigate to its editor).
 * Errors propagate to the caller.
 */
export const duplicateCurrentPath =
  (input: { name?: string } = {}): PathBuilderGraphThunk<Promise<number>> =>
  async (_dispatch, getState) => {
    const pathId = getState().pathBuilderGraph.pathId;
    if (pathId === null) {
      throw new Error(
        "pathBuilderGraph: no path loaded — dispatch loadPathGraph first",
      );
    }
    const request: { name?: string } = {};
    if (input.name !== undefined) {
      request.name = input.name;
    }
    const response = await duplicateLearningPath(pathId, request);
    return response.data.path.id;
  };

/**
 * Applies a node-drag position immediately (optimistic) and buffers the
 * change; after 500ms of quiet, all buffered changes coalesce into ONE
 * PATCH /steps/positions batch (latest coordinates per step win).
 * canvasX/canvasY null = revert the step to dagre auto-layout.
 */
export const queueStepPositionChange =
  (input: {
    stepClientId: string;
    canvasX: number | null;
    canvasY: number | null;
  }): PathBuilderGraphThunk =>
  (dispatch, getState) => {
    assertPathLoaded(getState());
    if (getState().pathBuilderGraph.steps[input.stepClientId] === undefined) {
      return;
    }
    dispatch(
      actions.stepPositionsPatchedLocally({
        positions: [
          {
            clientId: input.stepClientId,
            canvasX: input.canvasX,
            canvasY: input.canvasY,
          },
        ],
      }),
    );
    pendingPositionChanges.set(input.stepClientId, {
      canvasX: input.canvasX,
      canvasY: input.canvasY,
    });
    clearPositionDebounce();
    positionDebounceTimer = setTimeout(() => {
      positionDebounceTimer = null;
      dispatch(flushStepPositionChanges());
    }, POSITION_DEBOUNCE_MS);
  };

/**
 * Forces the buffered position changes into the queue immediately (e.g.,
 * before navigation or right after an Auto-layout pass).
 */
export const flushStepPositionChanges =
  (): PathBuilderGraphThunk => (dispatch, getState) => {
    clearPositionDebounce();
    if (pendingPositionChanges.size === 0) {
      return;
    }
    if (getState().pathBuilderGraph.pathId === null) {
      pendingPositionChanges = new Map();
      return;
    }
    const positions = Array.from(pendingPositionChanges.entries()).map(
      ([stepClientId, coordinates]) => ({
        stepClientId,
        canvasX: coordinates.canvasX,
        canvasY: coordinates.canvasY,
      }),
    );
    pendingPositionChanges = new Map();
    dispatch(
      actions.opEnqueued({
        opId: nanoid(),
        kind: "updateStepPositions",
        positions,
      }),
    );
    void dispatch(pumpQueue());
  };

// --- Selectors -----------------------------------------------------------------------

const selectSlice = (state: PathBuilderGraphRootState): PathBuilderGraphState =>
  state.pathBuilderGraph;

export const selectGraphPathId = (
  state: PathBuilderGraphRootState,
): number | null => selectSlice(state).pathId;

export const selectGraphLoadStatus = (
  state: PathBuilderGraphRootState,
): GraphLoadStatus => selectSlice(state).loadStatus;

export const selectGraphLoadError = (
  state: PathBuilderGraphRootState,
): string | null => selectSlice(state).loadError;

export const selectGraphMeta = (
  state: PathBuilderGraphRootState,
): BuilderMeta => selectSlice(state).meta;

export const selectBuilderSteps = (
  state: PathBuilderGraphRootState,
): Record<string, BuilderStep> => selectSlice(state).steps;

export const selectBuilderConnections = (
  state: PathBuilderGraphRootState,
): Record<string, BuilderConnection> => selectSlice(state).connections;

export const selectBuilderItems = (
  state: PathBuilderGraphRootState,
): Record<string, BuilderItem> => selectSlice(state).items;

/**
 * Course IDs currently present in the path. A course may appear at most once
 * per path (enforced server-side, 409 on violation), so the builder palette
 * uses this to stop offering courses that are already in the path. Confirmed
 * items only — a just-dropped course joins the set once its create resolves
 * (its payload arrives); the sub-second in-flight gap is backstopped by the
 * API's duplicate guard.
 */
export const selectCourseIdsInPath = createSelector(
  [selectBuilderItems],
  (items): ReadonlySet<number> => {
    const courseIds = new Set<number>();
    for (const item of Object.values(items)) {
      if (item.type !== "course" || item.payload === null) {
        continue;
      }
      const courseId = (item.payload as { courseId?: number }).courseId;
      if (typeof courseId === "number") {
        courseIds.add(courseId);
      }
    }
    return courseIds;
  },
);

export const selectBuilderSelection = (
  state: PathBuilderGraphRootState,
): BuilderSelection => selectSlice(state).selection;

export const selectGraphSaveState = (
  state: PathBuilderGraphRootState,
): BuilderSaveState => selectSlice(state).save;

export const selectServerUpdatedAt = (
  state: PathBuilderGraphRootState,
): string => selectSlice(state).meta.serverUpdatedAt;

/** isDirty == pendingOps > 0 (navigation guard hook point). */
export const selectGraphIsDirty = (state: PathBuilderGraphRootState): boolean =>
  selectSlice(state).save.pendingOps > 0;

/** True when undoGraphEdit has an entry to replay (Ctrl+Z enablement). */
export const selectCanUndo = (state: PathBuilderGraphRootState): boolean =>
  selectSlice(state).history.past.length > 0;

/** True when redoGraphEdit has an entry to replay (Ctrl+Shift+Z enablement). */
export const selectCanRedo = (state: PathBuilderGraphRootState): boolean =>
  selectSlice(state).history.future.length > 0;

/** Undo stack depth (capped at HISTORY_DEPTH). */
export const selectUndoDepth = (state: PathBuilderGraphRootState): number =>
  selectSlice(state).history.past.length;

/** Redo stack depth. */
export const selectRedoDepth = (state: PathBuilderGraphRootState): number =>
  selectSlice(state).history.future.length;

/** Steps ordered by sortKey, then serverId (new steps last within a key). */
export const selectOrderedSteps = createSelector(
  [selectBuilderSteps],
  (steps): BuilderStep[] =>
    Object.values(steps).sort(
      (a, b) =>
        a.sortKey - b.sortKey ||
        (a.serverId ?? Number.MAX_SAFE_INTEGER) -
          (b.serverId ?? Number.MAX_SAFE_INTEGER),
    ),
);

export const selectConnectionList = createSelector(
  [selectBuilderConnections],
  (connections): BuilderConnection[] => Object.values(connections),
);

/**
 * Items of one step, in itemIds order. Returns a new array per call — wrap
 * in a memoized selector (or read step.itemIds + selectBuilderItems) in
 * render-hot paths.
 */
export const selectStepItems = (
  state: PathBuilderGraphRootState,
  stepClientId: string,
): BuilderItem[] => {
  const slice = selectSlice(state);
  const step = slice.steps[stepClientId];
  if (step === undefined) {
    return [];
  }
  const result: BuilderItem[] = [];
  step.itemIds.forEach((itemId) => {
    const item = slice.items[itemId];
    if (item !== undefined) {
      result.push(item);
    }
  });
  return result;
};

export default pathBuilderGraphSlice.reducer;
